import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GuardianSidebar from '../components/GuardianSidebar';
import Toast, { useToast } from '../components/Toast';
import { useTheme } from '../context/ThemeContext';
import { useGuardianLanguage } from '../context/GuardianLanguageContext';
import { useReportLang } from '../hooks/useReportLang';
import translations from '../i18n/translations';
import { guardianFetch } from '../utils/authFetch';
import { logoutAndClearLocal } from '../api/authLogout';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ──────────────────────────────────── report translations ───────────── */

const reportTranslations = {
  ar: {
    monthlyReportTitle: 'التقرير الشهري',
    weeklyReportTitle: 'التقرير الأسبوعي',
    sessionReportTitle: 'تقرير جلسة',
    quizReportTitle: 'تقرير اختبار',
    student: 'الطالب',
    grade: 'الصف',
    executiveSummary: 'الملخص التنفيذي',
    totalSessions: 'إجمالي الجلسات',
    avgPerformance: 'متوسط الأداء',
    longestStreak: 'أطول سلسلة',
    completedUnits: 'الوحدات المكتملة',
    performanceBySubject: 'الأداء لكل مادة',
    cambridgeSection: 'القسم الأول: مواد كامبريدج',
    qatarSection: 'القسم الثاني: المواد القطرية',
    qatarNote: 'يتم تقييم المواد القطرية بمعايير مستقلة ولا تُقارن بمواد كامبريدج',
    strongPoints: 'نقاط القوة',
    weakPoints: 'نقاط الضعف',
    recommendation: 'التوصية',
    streakTitle: 'الانتظام والمثابرة',
    pedagogicalTitle: 'التوصيات التربوية',
    sessionSummary: 'ملخص الجلسة',
    conceptsCovered: 'المفاهيم التي تم شرحها',
    questionsDetail: 'تفاصيل أسئلة الفهم',
    weaknessesDetected: 'نقاط الضعف المكتشفة',
    nextSession: 'توصية للجلسة القادمة',
    quizOverview: 'نظرة عامة على الاختبار',
    axisPerformance: 'الأداء لكل محور',
    questionBreakdown: 'تفاصيل الأسئلة',
    comparison: 'مقارنة بالاختبار السابق',
    exportPDF: 'تصدير PDF',
    sendEmail: 'إرسال بالبريد',
    close: 'إغلاق',
    weaknessOccurrence: 'تكررت',
    weaknessLastSeen: 'آخر ظهور',
    times: 'مرة',
    days: 'يوم',
    session: 'جلسة',
    correct: 'صحيح',
    wrong: 'خطأ',
    all: 'الكل',
    showMore: 'عرض باقي',
    questions: 'أسئلة',
    understood: 'فهم جيد',
    needsReview: 'يحتاج مراجعة',
    notUnderstood: 'لم يفهم',
    notTaughtYet: 'لم يُدرَّس بعد',
    readinessLabel: 'جاهزية الطالب للموضوع التالي',
    autoSavedNote: 'تم حفظ نقاط الضعف هذه تلقائياً وستؤثر على خطة الجلسات القادمة',
    preparedBy: 'تم إعداد هذا التقرير تلقائياً بواسطة جهبذ',
    dataNote: 'جميع البيانات مبنية على جلسات التعلم الفعلية للطالب',
    subjectNames: {
      'الرياضيات': 'الرياضيات',
      'العلوم': 'العلوم',
      'اللغة الإنجليزية': 'اللغة الإنجليزية',
      'التربية الإسلامية': 'التربية الإسلامية',
    },
    actionPlanTitle: '📋 خطة العمل التربوية',
    actionPlanEmpty: 'لا توجد نقاط ضعف تستدعي خطة عمل في هذا التقرير',
    priorityHigh: '🔴 أولوية عالية',
    priorityMedium: '🟡 أولوية متوسطة',
    priorityLow: '🟢 أولوية منخفضة',
    guardianBlockLabel: 'لك أنت — ولي الأمر',
    jahbathBlockLabel: 'جهبذ سيقوم بـ',
    occurrenceLabel: 'تكرر',
    occurrenceSuffix: 'مرة',
    actionTypes: {
      home_activity: 'نشاط منزلي',
      conversation: 'محادثة',
      observation: 'متابعة',
      reinforcement: 'تعزيز',
      review: 'مراجعة',
      restructure: 'إعادة هيكلة',
      increase_difficulty: 'رفع مستوى',
      visual_explanation: 'شرح بصري',
      targeted_practice: 'تدريب مكثف',
    },
  },
  en: {
    monthlyReportTitle: 'Monthly Report',
    weeklyReportTitle: 'Weekly Report',
    sessionReportTitle: 'Session Report',
    quizReportTitle: 'Quiz Report',
    student: 'Student',
    grade: 'Grade',
    executiveSummary: 'Executive Summary',
    totalSessions: 'Total Sessions',
    avgPerformance: 'Avg Performance',
    longestStreak: 'Longest Streak',
    completedUnits: 'Completed Units',
    performanceBySubject: 'Performance by Subject',
    cambridgeSection: 'Section 1: Cambridge Subjects',
    qatarSection: 'Section 2: Qatari Subjects',
    qatarNote: 'Qatari subjects are assessed independently and are not compared to Cambridge subjects',
    strongPoints: 'Strong Points',
    weakPoints: 'Weak Points',
    recommendation: 'Recommendation',
    streakTitle: 'Consistency & Streak',
    pedagogicalTitle: 'Pedagogical Recommendations',
    sessionSummary: 'Session Summary',
    conceptsCovered: 'Concepts Covered',
    questionsDetail: 'Comprehension Questions Detail',
    weaknessesDetected: 'Weaknesses Detected',
    nextSession: 'Next Session Recommendation',
    quizOverview: 'Quiz Overview',
    axisPerformance: 'Performance by Axis',
    questionBreakdown: 'Question Breakdown',
    comparison: 'Comparison with Previous Quiz',
    exportPDF: 'Export PDF',
    sendEmail: 'Send by Email',
    close: 'Close',
    weaknessOccurrence: 'Occurred',
    weaknessLastSeen: 'Last seen',
    times: 'times',
    days: 'days',
    session: 'session',
    correct: 'Correct',
    wrong: 'Wrong',
    all: 'All',
    showMore: 'Show remaining',
    questions: 'questions',
    understood: 'Understood',
    needsReview: 'Needs Review',
    notUnderstood: 'Not Understood',
    notTaughtYet: 'Not taught yet',
    readinessLabel: 'Student readiness for next topic',
    autoSavedNote: 'These weaknesses have been automatically saved and will affect future session planning',
    preparedBy: 'This report was automatically prepared by Jahbath',
    dataNote: "All data is based on the student's actual learning sessions",
    subjectNames: {
      'الرياضيات': 'Mathematics',
      'العلوم': 'Science',
      'اللغة الإنجليزية': 'English',
      'التربية الإسلامية': 'Islamic Education',
    },
    actionPlanTitle: '📋 Pedagogical Action Plan',
    actionPlanEmpty: 'No weaknesses requiring an action plan in this report',
    priorityHigh: '🔴 High Priority',
    priorityMedium: '🟡 Medium Priority',
    priorityLow: '🟢 Low Priority',
    guardianBlockLabel: 'For You — Guardian',
    jahbathBlockLabel: 'Jahbath Will',
    occurrenceLabel: 'Occurred',
    occurrenceSuffix: 'times',
    actionTypes: {
      home_activity: 'Home Activity',
      conversation: 'Conversation',
      observation: 'Observation',
      reinforcement: 'Reinforcement',
      review: 'Review',
      restructure: 'Restructure',
      increase_difficulty: 'Level Up',
      visual_explanation: 'Visual',
      targeted_practice: 'Targeted Practice',
    },
  },
};

/* ──────────────────────────────────────── weakness info ─────────────── */

const WEAKNESS_INFO = {
  'الكسور العشرية':    { desc_ar: 'يخطئ في تحويل الكسور وترتيبها على خط الأعداد',            desc_en: 'Errors converting fractions and ordering them on the number line',        count: 2, last: '15 مارس 2026' },
  'دورة الماء':        { desc_ar: 'يخلط بين مراحل التبخر والتكثف',                              desc_en: 'Confuses evaporation and condensation stages',                             count: 3, last: '12 مارس 2026' },
  'سلاسل الغذاء':     { desc_ar: 'صعوبة في تحديد المنتج والمستهلك والمحلل',                    desc_en: 'Difficulty identifying producer, consumer, and decomposer',                count: 2, last: '18 مارس 2026' },
  'الضوء والظل':       { desc_ar: 'لا يفهم كيف تتكون الظلال بشكل صحيح',                        desc_en: 'Does not understand how shadows form correctly',                           count: 1, last: '20 مارس 2026' },
  'قواعد الفعل الماضي':{ desc_ar: 'يخطئ في تصريف الأفعال غير المنتظمة',                        desc_en: 'Errors conjugating irregular past tense verbs',                            count: 2, last: '10 مارس 2026' },
  'قوانين نيوتن':          { desc_ar: 'صعوبة في تطبيق القانون الثالث في سياقات مختلفة',               desc_en: "Difficulty applying Newton's third law in different contexts",                count: 1, last: '1 مارس 2026'   },
  'جمع الكسور العشرية':     { desc_ar: 'يخطئ في حمل الأرقام عند جمع الكسور العشرية',                    desc_en: 'Errors carrying numbers when adding decimal fractions',                    count: 2, last: '5 مارس 2026'   },
  'مقارنة الكسور العشرية': { desc_ar: 'يعتقد أن الكسر الأطول رقماً هو الأكبر دائماً',                  desc_en: 'Believes the longer decimal number is always the larger value',             count: 1, last: '5 مارس 2026'   },
  'دورة الماء — التكثف':      { desc_ar: 'يخلط بين مراحل التبخر والتكثف ويعتقد أنهما نفس العملية',     desc_en: 'Confuses evaporation and condensation and treats them as the same process', count: 2, last: '10 مارس 2026' },
  'سلاسل الغذاء — المنتج':   { desc_ar: 'لا يفرق بين المنتج (النبات) والمستهلك (الحيوان)',               desc_en: 'Cannot distinguish the producer (plant) from the consumer (animal)',       count: 2, last: '10 مارس 2026' },
  'الظل وزاوية الشمس':        { desc_ar: 'لا يربط زاوية الشمس بطول الظل وشكله',                         desc_en: 'Cannot link the sun angle to the length and shape of shadows',            count: 1, last: '10 مارس 2026' },
  'المحلل في سلاسل الغذاء':   { desc_ar: 'لا يعرف دور المحلل في النظام البيئي',                          desc_en: 'Does not understand the role of decomposers in the ecosystem',            count: 1, last: '10 مارس 2026' },
};

/* ──────────────────────────────────── subject radar data ────────────── */

const SUBJECT_RADAR = {
  'الرياضيات':          { axes_ar: ['الأعداد','الجبر','الهندسة','القياس','الإحصاء'],                      axes_en: ['Numbers','Algebra','Geometry','Measurement','Statistics'],       scores: [85,70,90,65,75] },
  'العلوم':              { axes_ar: ['المواد وخصائصها','الكائنات الحية','الأرض والفضاء','الطاقة','البيئة'], axes_en: ['Matter','Living Things','Earth & Space','Energy','Environment'],  scores: [75,55,60,70,50] },
  'اللغة الإنجليزية':   { axes_ar: ['القراءة','الكتابة','المفردات','القواعد','الاستماع'],                  axes_en: ['Reading','Writing','Vocabulary','Grammar','Listening'],           scores: [80,65,85,60,72] },
  'التربية الإسلامية':  { axes_ar: ['العقيدة','الفقه','السيرة','القرآن الكريم','الأخلاق'],                 axes_en: ['Aqeedah','Fiqh','Seerah','Quran','Morals'],                       scores: [90,85,88,95,92] },
  'اللغة العربية':       { axes_ar: ['القراءة','الكتابة','الإملاء','النحو','المحادثة'],                     axes_en: ['Reading','Writing','Spelling','Grammar','Speaking'],              scores: [91,88,95,82,90] },
  'الاجتماعيات':         { axes_ar: ['الجغرافيا','التاريخ','التربية الوطنية','البيئة','الثقافة'],           axes_en: ['Geography','History','Civics','Environment','Culture'],           scores: [60,55,65,50,60] },
  'الفيزياء':            { axes_ar: ['الحركة','القوى','الطاقة','الكهرباء','الموجات'],                      axes_en: ['Motion','Forces','Energy','Electricity','Waves'],                 scores: [85,80,88,78,84] },
};

/* ─────────────────────────────────────────────────────── dummy data ──── */

const DUMMY_REPORTS = [
  {
    id: 'dummy-monthly-001',
    report_type: 'monthly_report',
    student_name: 'زياد محمد',
    student_name_en: 'Ziad Mohammed',
    created_at: '2026-03-01T08:00:00Z',
    grade: 7,
    school: 'مدرسة قطر النموذجية',
    school_en: 'Qatar Model School',
    avg_score: 75,
    sessions_total: 18,
    sessions_this_month: 12,
    total_questions: 214,
    correct_answers: 158,
    weaknesses_count: 3,
    streak_days: 9,
    active_days: [1,2,3,4,5,6,7,8,9,12,13,15,18,19,20,22,25,27],
    subjects: [
      {
        name: 'الرياضيات', name_en: 'Mathematics', emoji: '📐', curriculum: 'cambridge',
        score: 82, sessions: 5, correct_answers: 48, total_questions: 60,
        last_session: '2026-03-05',
        weaknesses: [],
        strong_points_ar: ['حل المسائل بخطوات منظمة','ضرب الأعداد الكبيرة بدقة'],
        strong_points_en: ['Solving problems step-by-step','Multiplying large numbers accurately'],
        rec_ar: 'في مادة الرياضيات، زياد يُظهر أداءً ممتازاً — يُنصح بالتدرج نحو مسائل الجبر المتقدمة.',
        rec_en: 'In Mathematics, Ziad shows excellent performance — progress to advanced algebra problems.',
      },
      {
        name: 'العلوم', name_en: 'Science', emoji: '🔬', curriculum: 'cambridge',
        score: 69, sessions: 4, correct_answers: 30, total_questions: 44,
        last_session: '2026-03-03',
        weaknesses: [{ name: 'دورة الماء', name_en: 'Water Cycle' }, { name: 'سلاسل الغذاء', name_en: 'Food Chains' }],
        strong_points_ar: ['فهم خصائص المادة','تجارب الكهرباء البسيطة'],
        strong_points_en: ['Understanding matter properties','Basic electricity experiments'],
        rec_ar: 'في مادة العلوم، يُنصح بمراجعة دورة الماء وسلاسل الغذاء عبر جلسة تركيز خاصة.',
        rec_en: 'In Science, review water cycle and food chains with a dedicated focus session.',
      },
      {
        name: 'اللغة العربية', name_en: 'Arabic', emoji: '📖', curriculum: 'qatar',
        score: 91, sessions: 3, correct_answers: 40, total_questions: 44,
        last_session: '2026-02-28',
        weaknesses: [],
        strong_points_ar: ['الإملاء والتشكيل','الفهم القرائي المتقدم'],
        strong_points_en: ['Spelling and diacritics','Advanced reading comprehension'],
        rec_ar: 'في مادة اللغة العربية، الأداء ممتاز — استمر في التحفيز والمشاركة في القراءة.',
        rec_en: 'In Arabic, performance is excellent — keep motivating and encourage reading participation.',
      },
      {
        name: 'الاجتماعيات', name_en: 'Social Studies', emoji: '🗺️', curriculum: 'qatar',
        score: 58, sessions: 2, correct_answers: 22, total_questions: 38,
        last_session: '2026-02-25',
        weaknesses: [{ name: 'الضوء والظل', name_en: 'Light and Shadow' }],
        strong_points_ar: ['حفظ أسماء الدول العربية'],
        strong_points_en: ['Memorizing Arab country names'],
        rec_ar: 'في مادة الاجتماعيات، يحتاج زياد إلى اهتمام خاص — خصّص جلستين إضافيتين هذا الأسبوع.',
        rec_en: 'In Social Studies, Ziad needs extra attention — schedule two additional sessions this week.',
      },
    ],
    recommendations: [
      { type: 'green',  text_ar: 'زياد يمتلك قدرة رياضية واضحة في مادة الرياضيات — أداؤه يستحق التقدير والتحفيز المستمر.', text_en: 'Ziad shows clear mathematical strength in Mathematics — his performance deserves recognition.' },
      { type: 'amber',  text_ar: 'مادة العلوم تحتاج اهتماماً خاصاً — يُنصح بجلستين إضافيتين للتركيز على نقاط الضعف.', text_en: 'Science needs special attention — two extra sessions are recommended to address weak points.' },
      { type: 'blue',   text_ar: 'لاحظنا انقطاعاً في منتصف الشهر عبر جميع المواد — شجّعه على الانتظام اليومي.', text_en: 'We noticed a mid-month gap across all subjects — encourage daily consistency.' },
      { type: 'orange', text_ar: 'امتحان نهاية الفصل بعد 6 أسابيع. ابدأ بمراجعة مادة العلوم والاجتماعيات أولاً.', text_en: 'End-of-term exam in 6 weeks. Start by reviewing Science and Social Studies first.' },
      { type: 'purple', text_ar: 'زياد يستحق التقدير على أدائه الممتاز في التربية الإسلامية والرياضيات — شاركه نتائجه الإيجابية.', text_en: 'Ziad deserves praise for excellent performance in Islamic Education and Mathematics — share his positive results.' },
    ],
    action_plan: {
      action_plan: [
        {
          priority: 'high',
          subject: 'الرياضيات',
          unit: 'الكسور',
          topic: 'الكسور العشرية',
          weakness_name: 'جمع الكسور العشرية',
          occurrence_count: 3,
          directed_to: 'both',
          guardian_block: {
            opening: 'ولي الأمر العزيز،',
            educational_context: 'في سن الصف الرابع، يميل الأطفال إلى التعامل مع الأعداد العشرية كأعداد صحيحة منفصلة، مما يؤدي إلى أخطاء في الجمع. هذا سلوك طبيعي في هذه المرحلة ويُعالج بالربط الحسي المباشر.',
            actions: [
              {
                action_text: 'ضع أمام زياد عملات معدنية — 5 هللات و3 هللات — واطلب منه جمعها. حين يحسبها بيده ويصل إلى 8 هللات، سيفهم أن 0.5 + 0.3 = 0.8 بشكل طبيعي دون حفظ.',
                action_type: 'home_activity',
              },
              {
                action_text: 'عند مراجعة الواجب معه، لا تصحح الخطأ مباشرة — اسأله أولاً: هل الناتج أكبر أم أصغر من الرقمين اللذين جمعتهما؟ هذا السؤال يبني حس الاتزان العددي لديه.',
                action_type: 'conversation',
              },
            ],
          },
          jahbath_block: {
            actions: [
              {
                action_text: 'سيبدأ كل جلسة رياضيات بسؤال مراجعة واحد على جمع الكسور العشرية قبل الانتقال لأي موضوع جديد، حتى يسجل زياد 3 إجابات صحيحة متتالية.',
                action_type: 'targeted_practice',
              },
              {
                action_text: 'سيقدم شرحاً بصرياً بخط الأعداد في الجلسة القادمة لتوضيح موقع الكسور العشرية وعلاقتها ببعض.',
                action_type: 'visual_explanation',
              },
            ],
          },
          exam_note: null,
        },
        {
          priority: 'medium',
          subject: 'العلوم',
          unit: 'الماء والبيئة',
          topic: 'دورة الماء',
          weakness_name: 'التكثف في دورة الماء',
          occurrence_count: 2,
          directed_to: 'both',
          guardian_block: {
            opening: 'ولي الأمر العزيز،',
            educational_context: 'الخلط بين التبخر والتكثف شائع في هذه المرحلة لأن الطفل يرى التبخر بوضوح لكنه نادراً ما يلاحظ التكثف في حياته اليومية. المشاهدة المباشرة هي الحل الأسرع.',
            actions: [
              {
                action_text: 'ضع كوباً مثلجاً في الهواء الدافئ معه وراقبا معاً قطرات الماء التي تتشكل على جدار الكوب من الخارج. اسأله: من أين جاء هذا الماء؟ دعه يفكر ويجيب قبل أن تشرح.',
                action_type: 'home_activity',
              },
            ],
          },
          jahbath_block: {
            actions: [
              {
                action_text: 'سيعيد شرح مرحلة التكثف بأسلوب مختلف في الجلسة القادمة مع صورة توضيحية تظهر دورة الماء كاملة، مع التركيز على الفرق البصري بين التبخر والتكثف.',
                action_type: 'visual_explanation',
              },
            ],
          },
          exam_note: null,
        },
        {
          priority: 'low',
          subject: 'اللغة الإنجليزية',
          unit: 'الأفعال',
          topic: 'الفعل الماضي',
          weakness_name: 'قواعد الفعل الماضي غير المنتظم',
          occurrence_count: 1,
          directed_to: 'jahbath',
          guardian_block: null,
          jahbath_block: {
            actions: [
              {
                action_text: 'سيراقب هذه النقطة في الجلسة القادمة — ظهرت مرة واحدة فقط ولا تُصنف بعد كضعف متكرر. إذا تكررت مرة أخرى سيتم إدراجها في خطة العمل بأولوية أعلى.',
                action_type: 'review',
              },
            ],
          },
          exam_note: null,
        },
      ],
    },
  },
  {
    id: 'dummy-weekly-002',
    report_type: 'weekly_report',
    student_name: 'سارة علي',
    student_name_en: 'Sara Ali',
    created_at: '2026-03-03T10:30:00Z',
    grade: 8,
    school: 'مدرسة الدوحة للبنات',
    school_en: 'Doha Girls School',
    avg_score: 89,
    sessions_total: 9,
    sessions_this_month: 6,
    total_questions: 120,
    correct_answers: 107,
    weaknesses_count: 1,
    streak_days: 6,
    active_days: [1,2,3,5,6,7],
    subjects: [
      {
        name: 'الرياضيات', name_en: 'Mathematics', emoji: '📐', curriculum: 'cambridge',
        score: 91, sessions: 3, correct_answers: 38, total_questions: 42,
        last_session: '2026-03-06',
        weaknesses: [],
        strong_points_ar: ['المعادلات الخطية','الهندسة التحليلية'],
        strong_points_en: ['Linear equations','Analytical geometry'],
        rec_ar: 'في مادة الرياضيات، سارة تتفوق بوضوح — انتقلي إلى تحديات الجبر المتقدم.',
        rec_en: 'In Mathematics, Sara excels clearly — move to advanced algebra challenges.',
      },
      {
        name: 'الفيزياء', name_en: 'Physics', emoji: '⚡', curriculum: 'cambridge',
        score: 83, sessions: 3, correct_answers: 29, total_questions: 35,
        last_session: '2026-03-05',
        weaknesses: [{ name: 'قوانين نيوتن', name_en: "Newton's Laws" }],
        strong_points_ar: ['مسائل الطاقة الحركية','فهم الموجات الصوتية'],
        strong_points_en: ['Kinetic energy problems','Understanding sound waves'],
        rec_ar: 'في مادة الفيزياء، يُنصح بمراجعة قوانين نيوتن مرة أخرى قبل الاختبار القادم.',
        rec_en: "In Physics, review Newton's Laws once more before the next exam.",
      },
      {
        name: 'اللغة الإنجليزية', name_en: 'English', emoji: '🌐', curriculum: 'cambridge',
        score: 95, sessions: 3, correct_answers: 40, total_questions: 43,
        last_session: '2026-03-04',
        weaknesses: [],
        strong_points_ar: ['القراءة والفهم','المفردات المتقدمة'],
        strong_points_en: ['Reading comprehension','Advanced vocabulary'],
        rec_ar: 'في مادة اللغة الإنجليزية، الأداء استثنائي — فكّري في المشاركة بمسابقات اللغة.',
        rec_en: 'In English, performance is exceptional — consider participating in language competitions.',
      },
    ],
    recommendations: [
      { type: 'green',  text_ar: 'سارة تمتلك قدرة رياضية واضحة في مادة الرياضيات — أداؤها يستحق التقدير والتشجيع.', text_en: 'Sara shows clear mathematical strength in Mathematics — her performance deserves recognition.' },
      { type: 'amber',  text_ar: 'مادة الفيزياء تحتاج اهتماماً خاصاً — قوانين نيوتن تحتاج مراجعة إضافية قبل الاختبار.', text_en: "Physics needs special attention — Newton's Laws require additional review before the exam." },
      { type: 'blue',   text_ar: 'لاحظنا انقطاعاً في بعض أيام الأسبوع عبر جميع المواد — شجّعيها على الانتظام اليومي.', text_en: 'We noticed gaps across some days this week — encourage daily consistency.' },
      { type: 'orange', text_ar: 'الاختبار القادم بعد أسبوعين. ابدأي بمراجعة مادة الفيزياء أولاً.', text_en: 'Next exam in two weeks. Start by reviewing Physics first.' },
      { type: 'purple', text_ar: 'سارة تستحق التقدير على أدائها الاستثنائي في اللغة الإنجليزية والرياضيات.', text_en: 'Sara deserves praise for exceptional performance in English and Mathematics.' },
    ],
  },
  {
    id: 'dummy-002',
    report_type: 'session_report',
    student_name: 'زياد',
    student_name_en: 'Ziad',
    created_at: '2026-03-05T15:30:00',
    grade: 4,
    subject_ar: 'الرياضيات',
    subject_en: 'Mathematics',
    unit_ar: 'الكسور',
    unit_en: 'Fractions',
    topic_ar: 'الكسور العشرية',
    topic_en: 'Decimal Fractions',
    duration_mins: 42,
    summary_ar: 'جلسة منتجة تناولت مفهوم الكسور العشرية في مادة الرياضيات. بدأت الجلسة بشرح مفصل مع أمثلة بصرية، تلتها أسئلة تحقق من الفهم. أظهر زياد فهماً جيداً للمبدأ الأساسي مع بعض الصعوبة في التطبيق.',
    summary_en: 'A productive session covering decimal fractions in Mathematics. The session began with a detailed explanation with visual examples, followed by comprehension check questions. Ziad showed good understanding of the basic principle with some difficulty in application.',
    concepts: [
      { name_ar: 'مفهوم الكسر العشري',                      name_en: 'Concept of Decimal Fractions',          status: 'good',   q_total: 3, q_correct: 3 },
      { name_ar: 'قراءة وكتابة الكسور العشرية',             name_en: 'Reading & Writing Decimal Fractions',   status: 'good',   q_total: 3, q_correct: 3 },
      { name_ar: 'ترتيب الكسور العشرية على خط الأعداد',     name_en: 'Ordering Decimals on Number Line',      status: 'review', q_total: 3, q_correct: 2 },
      { name_ar: 'جمع الكسور العشرية',                      name_en: 'Adding Decimal Fractions',              status: 'fail',   q_total: 3, q_correct: 1 },
    ],
    questions: [
      { q_ar: 'ما قيمة 0.5 على خط الأعداد؟',          q_en: 'What is the value of 0.5 on the number line?',      student_ar: 'النصف',           student_en: 'One half',           correct_ar: 'النصف',           correct_en: 'One half',                correct: true  },
      { q_ar: 'رتّب: 0.3، 0.7، 0.1 تصاعدياً',         q_en: 'Order 0.3, 0.7, 0.1 ascending',                    student_ar: '0.1، 0.3، 0.7',   student_en: '0.1, 0.3, 0.7',    correct_ar: '0.1، 0.3، 0.7',   correct_en: '0.1, 0.3, 0.7',          correct: true  },
      { q_ar: 'ما ناتج 0.4 + 0.3؟',                   q_en: 'What is 0.4 + 0.3?',                               student_ar: '0.6',              student_en: '0.6',               correct_ar: '0.7',              correct_en: '0.7',                    correct: false },
      { q_ar: 'حوّل 3/10 إلى كسر عشري',               q_en: 'Convert 3/10 to a decimal',                        student_ar: '0.3',              student_en: '0.3',               correct_ar: '0.3',              correct_en: '0.3',                    correct: true  },
      { q_ar: 'ما ناتج 1.2 + 0.8؟',                   q_en: 'What is 1.2 + 0.8?',                               student_ar: '1.9',              student_en: '1.9',               correct_ar: '2.0',              correct_en: '2.0',                    correct: false },
      { q_ar: 'أي الأعداد أكبر: 0.45 أم 0.5؟',        q_en: 'Which is larger: 0.45 or 0.5?',                    student_ar: '0.45',             student_en: '0.45',              correct_ar: '0.5',              correct_en: '0.5',                    correct: false },
    ],
    session_radar: {
      axes_ar: ['الأعداد','الجبر','الهندسة','القياس','الإحصاء'],
      axes_en: ['Numbers','Algebra','Geometry','Measurement','Statistics'],
      scores: [70, 50, 0, 0, 0],
    },
    session_weaknesses: [
      { name: 'جمع الكسور العشرية',  name_en: 'Adding Decimal Fractions' },
      { name: 'مقارنة الكسور العشرية', name_en: 'Comparing Decimal Fractions' },
    ],
    next_topic_ar: 'طرح الكسور العشرية',
    next_topic_en: 'Subtracting Decimal Fractions',
    next_topic_readiness: 45,
    next_rec_ar: 'بناءً على أداء زياد في هذه الجلسة، يُنصح بأن تبدأ الجلسة القادمة في مادة الرياضيات بمراجعة سريعة على جمع الكسور العشرية قبل الانتقال لموضوع جديد. جهبذ سيبدأ الجلسة القادمة بسؤال مراجعة تلقائياً.',
    next_rec_en: "Based on Ziad's performance in this session, the next session in Mathematics should begin with a quick review of adding decimal fractions before moving to a new topic. Jahbath will automatically start the next session with a review question.",
  },
  {
    id: 'dummy-003',
    report_type: 'quiz_report',
    student_name: 'زياد',
    student_name_en: 'Ziad',
    created_at: '2026-03-10T11:00:00',
    grade: 4,
    subject_ar: 'العلوم',
    subject_en: 'Science',
    quiz_type_ar: 'اختبار وحدة',
    quiz_type_en: 'Unit Quiz',
    total_questions: 20,
    correct: 13,
    score: 65,
    prev_score: 58,
    quiz_radar: {
      axes_ar: ['المواد وخصائصها','الكائنات الحية','الأرض والفضاء','الطاقة','البيئة'],
      axes_en: ['Matter','Living Things','Earth & Space','Energy','Environment'],
      scores: [80,55,60,70,45],
    },
    quiz_questions: [
      { n:1,  diff:'easy',   correct:true,  axis_ar:'المواد وخصائصها', axis_en:'Matter',       q_ar:'ما الحالات الثلاث للمادة؟',                     q_en:'What are the 3 states of matter?',                  student_ar:'صلبة، سائلة، غازية',                                      student_en:'Solid, liquid, gas',                                            answer_ar:'صلبة، سائلة، غازية',                                         answer_en:'Solid, liquid, gas'                                              },
      { n:2,  diff:'easy',   correct:true,  axis_ar:'الأرض والفضاء',   axis_en:'Earth & Space',q_ar:'ما اسم عملية تحول الماء لبخار؟',                q_en:'What is the process of water turning to vapor?',    student_ar:'التبخر',                                                          student_en:'Evaporation',                                                    answer_ar:'التبخر',                                                          answer_en:'Evaporation'                                                     },
      { n:3,  diff:'medium', correct:false, axis_ar:'الأرض والفضاء',   axis_en:'Earth & Space',q_ar:'ما المرحلة التي يعود فيها البخار لماء؟',         q_en:'What stage turns vapor back to water?',             student_ar:'التبخر',                                                          student_en:'Evaporation',                                                    answer_ar:'التكثف',                                                          answer_en:'Condensation'                                                    },
      { n:4,  diff:'medium', correct:true,  axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'ما تعريف سلسلة الغذاء؟',                        q_en:'What is the definition of a food chain?',           student_ar:'سلسلة تربط الكائنات الحية بمصادر غذائها',                    student_en:'A chain linking living things to their food sources',           answer_ar:'سلسلة تربط الكائنات الحية بمصادر غذائها',                     answer_en:'A chain linking living things to their food sources'            },
      { n:5,  diff:'medium', correct:false, axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'من هو المنتج في سلسلة الغذاء؟',                 q_en:'Who is the producer in a food chain?',              student_ar:'الأسد',                                                           student_en:'The lion',                                                       answer_ar:'النبات',                                                          answer_en:'The plant'                                                       },
      { n:6,  diff:'hard',   correct:false, axis_ar:'الطاقة',           axis_en:'Energy',       q_ar:'لماذا تختفي الظلال عند الظهر؟',                 q_en:'Why do shadows disappear at noon?',                  student_ar:'لأن الشمس تغيب',                                                  student_en:'Because the sun sets',                                           answer_ar:'لأن الشمس تكون عمودية على الأرض',                               answer_en:'Because the sun is directly overhead'                            },
      { n:7,  diff:'medium', correct:true,  axis_ar:'الطاقة',           axis_en:'Energy',       q_ar:'ما مصدر طاقة النبات؟',                           q_en:"What is the plant's energy source?",                student_ar:'الشمس',                                                           student_en:'The sun',                                                        answer_ar:'الشمس',                                                           answer_en:'The sun'                                                         },
      { n:8,  diff:'hard',   correct:false, axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'ما الفرق بين المستهلك والمحلل؟',                 q_en:'What is the difference between consumer and decomposer?', student_ar:'لا أعرف',                                                    student_en:"I don't know",                                                   answer_ar:'المستهلك يأكل الكائنات الحية، المحلل يحلل الكائنات الميتة',   answer_en:'Consumer eats living things, decomposer breaks down dead matter' },
      { n:9,  diff:'easy',   correct:true,  axis_ar:'المواد وخصائصها', axis_en:'Matter',       q_ar:'ما خاصية الماء السائل؟',                        q_en:'What property does liquid water have?',             student_ar:'يأخذ شكل الوعاء',                                                 student_en:'Takes the shape of its container',                               answer_ar:'يأخذ شكل الوعاء',                                                answer_en:'Takes the shape of its container'                                },
      { n:10, diff:'medium', correct:true,  axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'ماذا يحتاج النبات لصنع غذائه؟',                 q_en:'What does a plant need to make food?',              student_ar:'ضوء وماء وثاني أكسيد الكربون',                                   student_en:'Light, water, and CO2',                                          answer_ar:'ضوء وماء وثاني أكسيد الكربون',                                  answer_en:'Light, water, and CO2'                                           },
      { n:11, diff:'easy',   correct:true,  axis_ar:'الطاقة',           axis_en:'Energy',       q_ar:'ما نوع الطاقة التي يحولها النبات؟',              q_en:'What type of energy does a plant convert?',         student_ar:'الطاقة الضوئية إلى كيميائية',                                    student_en:'Light energy to chemical energy',                                answer_ar:'الطاقة الضوئية إلى كيميائية',                                   answer_en:'Light energy to chemical energy'                                 },
      { n:12, diff:'medium', correct:false, axis_ar:'البيئة',           axis_en:'Environment',  q_ar:'ما تأثير تلوث الماء على السلاسل الغذائية؟',     q_en:'What is the effect of water pollution on food chains?', student_ar:'لا يؤثر عليها',                                              student_en:'No effect',                                                      answer_ar:'يدمر السلاسل الغذائية ويهدد الكائنات الحية',                   answer_en:'Destroys food chains and threatens living things'                },
      { n:13, diff:'easy',   correct:true,  axis_ar:'المواد وخصائصها', axis_en:'Matter',       q_ar:'ما حالة الجليد؟',                               q_en:'What state is ice in?',                             student_ar:'صلبة',                                                            student_en:'Solid',                                                          answer_ar:'صلبة',                                                            answer_en:'Solid'                                                           },
      { n:14, diff:'medium', correct:true,  axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'ما دور المستهلك الأول في السلسلة الغذائية؟',    q_en:'What is the role of the primary consumer?',         student_ar:'يأكل النباتات مباشرة',                                            student_en:'Eats plants directly',                                           answer_ar:'يأكل النباتات مباشرة',                                           answer_en:'Eats plants directly'                                            },
      { n:15, diff:'hard',   correct:false, axis_ar:'البيئة',           axis_en:'Environment',  q_ar:'كيف تؤثر الكائنات المتحللة على التربة؟',        q_en:'How do decomposers affect soil?',                   student_ar:'تؤثر سلباً',                                                     student_en:'Negatively',                                                     answer_ar:'تُثري التربة بالمواد الغذائية',                                  answer_en:'Enrich the soil with nutrients'                                  },
      { n:16, diff:'easy',   correct:true,  axis_ar:'الطاقة',           axis_en:'Energy',       q_ar:'من أين تحصل الكائنات الحية على الطاقة؟',        q_en:'Where do living things get energy from?',           student_ar:'من الغذاء',                                                       student_en:'From food',                                                      answer_ar:'من الغذاء',                                                       answer_en:'From food'                                                       },
      { n:17, diff:'medium', correct:true,  axis_ar:'الأرض والفضاء',   axis_en:'Earth & Space',q_ar:'ما المقصود بدورة الماء في الطبيعة؟',            q_en:'What is the water cycle in nature?',                student_ar:'تبخر وتكثف وهطول مستمر',                                         student_en:'Continuous evaporation, condensation, and precipitation',        answer_ar:'تبخر وتكثف وهطول مستمر',                                        answer_en:'Continuous evaporation, condensation, and precipitation'        },
      { n:18, diff:'hard',   correct:false, axis_ar:'الكائنات الحية',  axis_en:'Living Things',q_ar:'كيف تحافظ الأنظمة البيئية على توازنها؟',        q_en:'How do ecosystems maintain balance?',               student_ar:'بالصيد الجائر',                                                   student_en:'Through overhunting',                                            answer_ar:'بالتوازن بين المنتجين والمستهلكين والمحللين',                   answer_en:'By balancing producers, consumers, and decomposers'             },
      { n:19, diff:'medium', correct:true,  axis_ar:'المواد وخصائصها', axis_en:'Matter',       q_ar:'ما الفرق بين المادة الصلبة والسائلة؟',          q_en:'What is the difference between solid and liquid?',  student_ar:'الصلبة لها شكل ثابت، السائلة تأخذ شكل الوعاء',                  student_en:'Solid has fixed shape, liquid takes container shape',           answer_ar:'الصلبة لها شكل ثابت، السائلة تأخذ شكل الوعاء',                answer_en:'Solid has fixed shape, liquid takes container shape'            },
      { n:20, diff:'easy',   correct:true,  axis_ar:'الطاقة',           axis_en:'Energy',       q_ar:'ما مصدر طاقة الشمس؟',                           q_en:"What is the sun's energy source?",                  student_ar:'التفاعلات النووية',                                               student_en:'Nuclear reactions',                                              answer_ar:'التفاعلات النووية',                                              answer_en:'Nuclear reactions'                                               },
    ],
    quiz_weaknesses: [
      { name: 'دورة الماء — التكثف',    name_en: 'Water Cycle — Condensation'   },
      { name: 'سلاسل الغذاء — المنتج',  name_en: 'Food Chains — Producer'       },
      { name: 'الظل وزاوية الشمس',      name_en: 'Shadows and Sun Angle'        },
      { name: 'المحلل في سلاسل الغذاء', name_en: 'Decomposers in Food Chains'   },
    ],
    axis_comparison: [
      { axis_ar:'المواد وخصائصها', axis_en:'Matter',       prev:72, curr:80 },
      { axis_ar:'الكائنات الحية',  axis_en:'Living Things',prev:60, curr:55 },
      { axis_ar:'الأرض والفضاء',  axis_en:'Earth & Space', prev:50, curr:60 },
      { axis_ar:'الطاقة',          axis_en:'Energy',        prev:65, curr:70 },
      { axis_ar:'البيئة',          axis_en:'Environment',   prev:40, curr:45 },
    ],
    quiz_recommendations: [
      { type:'amber',  text_ar:'في مادة العلوم، نقاط ضعف زياد الرئيسية في دورة الماء وسلاسل الغذاء. يُنصح بجلسة مراجعة مخصصة لهذين الموضوعين قبل الاختبار القادم.', text_en:"In Science, Ziad's main weaknesses are the water cycle and food chains. A dedicated review session for both topics is recommended before the next exam." },
      { type:'blue',   text_ar:'أداء زياد في محور المواد وخصائصها تحسّن بشكل ملحوظ في مادة العلوم (+8%). هذا يدل على أن أسلوب المراجعة المتبع فعّال — استمر فيه.',     text_en:"Ziad's performance in the Matter axis improved notably in Science (+8%). This shows the current review method is effective — keep it up."                          },
      { type:'orange', text_ar:'الاختبار الشهري القادم في مادة العلوم بعد 3 أسابيع. جهبذ سيركز تلقائياً على دورة الماء وسلاسل الغذاء في الجلسات القادمة.',              text_en:"The next monthly Science exam is in 3 weeks. Jahbath will automatically focus on water cycle and food chains in upcoming sessions."                              },
      { type:'green',  text_ar:'رغم وجود نقاط ضعف في مادة العلوم، زياد يتحسن في كل اختبار. التحسن المستمر هو المؤشر الأهم — شجّعه على هذا التقدم.',                      text_en:"Despite weaknesses in Science, Ziad improves with every quiz. Continuous improvement is the most important indicator — encourage this progress."                 },
    ],
  },
  {
    id: 'dummy-004',
    report_type: 'weekly_report',
    student_name: 'زياد',
    student_name_en: 'Ziad',
    created_at: '2026-03-07T08:00:00',
    grade: 4,
    week_label_ar: 'الأسبوع الأول من مارس 2026',
    week_label_en: 'First Week of March 2026',
    sessions_count: 4,
    subjects_count: 3,
    avg_performance: 71,
    active_days_count: 5,
    week_days: [
      { day_ar: 'السبت',    day_en: 'Sat', active: true  },
      { day_ar: 'الأحد',    day_en: 'Sun', active: true  },
      { day_ar: 'الاثنين',  day_en: 'Mon', active: true  },
      { day_ar: 'الثلاثاء', day_en: 'Tue', active: false },
      { day_ar: 'الأربعاء', day_en: 'Wed', active: true  },
      { day_ar: 'الخميس',   day_en: 'Thu', active: true  },
      { day_ar: 'الجمعة',   day_en: 'Fri', active: false },
    ],
    sessions: [
      { date_ar: 'السبت 1 مارس',    date_en: 'Sat Mar 1', subject_ar: 'الرياضيات',        subject_en: 'Mathematics', topic_ar: 'الكسور العشرية', topic_en: 'Decimal Fractions',  score: 50, duration: 42 },
      { date_ar: 'الأحد 2 مارس',    date_en: 'Sun Mar 2', subject_ar: 'العلوم',            subject_en: 'Science',     topic_ar: 'دورة الماء',      topic_en: 'Water Cycle',        score: 60, duration: 38 },
      { date_ar: 'الاثنين 3 مارس',  date_en: 'Mon Mar 3', subject_ar: 'اللغة الإنجليزية', subject_en: 'English',     topic_ar: 'الفعل الماضي',    topic_en: 'Past Tense',         score: 75, duration: 35 },
      { date_ar: 'الأربعاء 5 مارس', date_en: 'Wed Mar 5', subject_ar: 'الرياضيات',        subject_en: 'Mathematics', topic_ar: 'جمع الكسور',       topic_en: 'Adding Fractions',   score: 65, duration: 40 },
    ],
    cambridge_subjects: [
      { name_ar: 'الرياضيات',        name_en: 'Mathematics', score: 57, change: -5 },
      { name_ar: 'العلوم',            name_en: 'Science',     score: 60, change:  8 },
      { name_ar: 'اللغة الإنجليزية', name_en: 'English',     score: 75, change:  3 },
    ],
    qatar_subjects: [
      { name_ar: 'التربية الإسلامية', name_en: 'Islamic Education', score: 88, change: 2 },
    ],
    weekly_weaknesses: [
      { name: 'جمع الكسور العشرية',  name_en: 'Adding Decimal Fractions' },
      { name: 'دورة الماء — التكثف', name_en: 'Water Cycle — Condensation' },
    ],
    weekly_recommendations: [
      { type: 'amber', text_ar: 'في مادة الرياضيات، تراجع أداء زياد هذا الأسبوع بسبب صعوبة في جمع الكسور. يُنصح بمراجعة قصيرة يومية لمدة 10 دقائق.', text_en: "In Mathematics, Ziad's performance declined this week due to difficulty adding fractions. A short 10-minute daily review is recommended." },
      { type: 'blue',  text_ar: 'اللغة الإنجليزية تحسّنت هذا الأسبوع. استمر في تشجيع زياد على القراءة اليومية بالإنجليزية.', text_en: 'English improved this week. Keep encouraging Ziad to read daily in English.' },
      { type: 'green', text_ar: '5 أيام نشاط من 7 هذا الأسبوع — نتيجة جيدة جداً! حاول الوصول لـ 6 أيام الأسبوع القادم.', text_en: '5 active days out of 7 this week — very good! Try to reach 6 days next week.' },
    ],
  },
];

/* ────────────────────────────────────────────────────── constants ───── */

const REPORT_TYPE_OPTIONS = [
  { value: '',               label: { ar: 'الكل',          en: 'All'            } },
  { value: 'session_report', label: { ar: 'تقرير جلسة',    en: 'Session Report' } },
  { value: 'weekly_report',  label: { ar: 'تقرير أسبوعي',  en: 'Weekly Report'  } },
  { value: 'monthly_report', label: { ar: 'تقرير شهري',    en: 'Monthly Report' } },
  { value: 'exam_report',    label: { ar: 'تقرير امتحان',   en: 'Exam Report'    } },
];

const TYPE_BADGE = {
  session_report: { label: { ar: 'تقرير جلسة',   en: 'Session'  }, color: '#06b6d4', bg: 'rgba(6,182,212,0.15)'   },
  weekly_report:  { label: { ar: 'تقرير أسبوعي', en: 'Weekly'   }, color: '#8b5cf6', bg: 'rgba(139,92,246,0.15)'  },
  monthly_report: { label: { ar: 'تقرير شهري',   en: 'Monthly'  }, color: '#22c55e', bg: 'rgba(34,197,94,0.15)'   },
  exam_report:    { label: { ar: 'تقرير امتحان',  en: 'Exam'     }, color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
  quiz_report:    { label: { ar: 'تقرير اختبار', en: 'Quiz'     }, color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
};

const REC_COLORS = {
  green:  { bg: 'rgba(34,197,94,0.12)',   border: '#22c55e', icon: '✓' },
  blue:   { bg: 'rgba(5,150,105,0.12)',   border: '#059669', icon: '→' },
  amber:  { bg: 'rgba(245,158,11,0.12)',  border: '#f59e0b', icon: '!' },
  red:    { bg: 'rgba(239,68,68,0.12)',   border: '#ef4444', icon: '⚠' },
  orange: { bg: 'rgba(249,115,22,0.12)',  border: '#f97316', icon: '◉' },
  purple: { bg: 'rgba(139,92,246,0.12)',  border: '#8b5cf6', icon: '★' },
};

/* ─────────────────────────────────────────────────── helpers ────────── */

function formatDate(iso, lang) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    }).format(new Date(iso));
  } catch { return iso.slice(0, 10); }
}

function formatShortDate(iso, lang) {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    }).format(new Date(iso));
  } catch { return iso.slice(0, 10); }
}

function scoreColor(score) {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
}

/* ─────────────────────────────── ProgressBar ───────────────────────── */

function ProgressBar({ value, color }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(value), 80);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <div style={{ height: 8, background: 'rgba(150,150,150,0.18)', borderRadius: 99, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${width}%`, background: color, borderRadius: 99, transition: 'width 0.7s cubic-bezier(0.22,1,0.36,1)' }} />
    </div>
  );
}

/* ─────────────────────────────── StreakCalendar ─────────────────────── */

function StreakCalendar({ activeDays, T }) {
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const activeSet = new Set(activeDays);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 32px)', gap: 6, justifyContent: 'start' }}>
      {days.map(d => {
        const active = activeSet.has(d);
        return (
          <div key={d} title={`${d} مارس`} style={{
            width: 32, height: 32, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: active ? 700 : 400,
            background: active ? 'rgba(5,150,105,0.85)' : 'rgba(150,150,150,0.12)',
            color: active ? '#fff' : T.textMuted || T.textSecondary,
            border: active ? '1.5px solid rgba(5,150,105,0.5)' : '1px solid transparent',
          }}>
            {d}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────── WeaknessPopup ────────────────────────── */

function WeaknessPopup({ data, lang, T, onClose }) {
  const info = WEAKNESS_INFO[data.weakness] || {};
  const desc = lang === 'ar' ? (info.desc_ar || data.weakness) : (info.desc_en || data.weakness);
  const last = lang === 'ar' ? `آخر ظهور: ${info.last || '—'}` : `Last occurred: ${info.last || '—'}`;
  const count = lang === 'ar' ? `تكررت ${info.count || 1} مرة` : `Occurred ${info.count || 1} time(s)`;

  useEffect(() => {
    const handler = () => onClose();
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const safeLeft = Math.min(data.x, window.innerWidth - 310);
  const safeTop  = data.y + 14 + 200 < window.innerHeight ? data.y + 14 : data.y - 210;

  return (
    <div
      onMouseDown={e => e.stopPropagation()}
      style={{
        position: 'fixed', left: safeLeft, top: safeTop, zIndex: 3000,
        background: T.bgCardSolid, border: `1.5px solid ${T.accent}`,
        borderRadius: 12, padding: '14px 16px', width: 280,
        boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        animation: 'wr-fadein 0.14s ease',
        direction: lang === 'ar' ? 'rtl' : 'ltr',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: T.textPrimary }}>{data.weakness}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
      </div>
      <div style={{ fontSize: 12, color: T.accent, marginBottom: 8, fontWeight: 600 }}>{data.subjectName}</div>
      <div style={{ fontSize: 13, color: T.textSecondary, lineHeight: 1.55, marginBottom: 8 }}>{desc}</div>
      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 2 }}>{count}</div>
      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10 }}>{last}</div>
      <div style={{ fontSize: 12, color: '#92400e', background: 'rgba(245,158,11,0.12)', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
        {lang === 'ar' ? 'يُنصح بمراجعة هذا المفهوم في الجلسة القادمة' : 'Recommended: review this concept in the next session'}
      </div>
    </div>
  );
}

/* ──────────────────────────── RadarChart (SVG) ─────────────────────── */

function RadarChart({ radarData, T, lang }) {
  const [tooltip, setTooltip] = useState(null);
  const N = 5;
  const cx = 140, cy = 140, R = 100;
  const axes   = lang === 'ar' ? radarData.axes_ar : radarData.axes_en;
  const scores = radarData.scores;
  const angle  = i => (2 * Math.PI / N) * i - Math.PI / 2;
  const px     = (i, r) => cx + r * Math.cos(angle(i));
  const py     = (i, r) => cy + r * Math.sin(angle(i));
  const polyPts = (r) => Array.from({ length: N }, (_, i) => `${px(i, r)},${py(i, r)}`).join(' ');
  const dataPts = scores.map((s, i) => `${px(i, R * s / 100)},${py(i, R * s / 100)}`).join(' ');

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <svg width="280" height="280" viewBox="0 0 280 280" style={{ maxWidth: '100%', overflow: 'visible' }}>
        {[0.25, 0.5, 0.75, 1].map((lvl, li) => (
          <polygon key={li} points={polyPts(R * lvl)} fill="none" stroke={T.border} strokeOpacity={0.45} strokeWidth={1} />
        ))}
        {Array.from({ length: N }, (_, i) => (
          <line key={i} x1={cx} y1={cy} x2={px(i, R)} y2={py(i, R)} stroke={T.border} strokeOpacity={0.3} strokeWidth={1} />
        ))}
        <polygon points={dataPts} fill={T.accent} fillOpacity={0.18} stroke={T.accent} strokeWidth={2} />
        {scores.map((s, i) => {
          const x    = px(i, R * s / 100);
          const y    = py(i, R * s / 100);
          const cosA = Math.cos(angle(i));
          const sinA = Math.sin(angle(i));
          const anchor = Math.abs(cosA) < 0.15 ? 'middle' : (cosA > 0 ? 'start' : 'end');
          return (
            <g key={i}>
              <circle
                cx={x} cy={y} r={5} fill={T.accent} style={{ cursor: 'pointer' }}
                onMouseEnter={e => setTooltip({ x: e.clientX, y: e.clientY, label: axes[i], score: s })}
                onMouseLeave={() => setTooltip(null)}
              />
              <text
                x={x + 8 * cosA} y={y + 8 * sinA + 3}
                fontSize={10} textAnchor={anchor} fontFamily="inherit" fill={T.textSecondary}
                paintOrder="stroke" stroke={T.bgCardSolid} strokeWidth={4} strokeLinejoin="round"
              >{s}%</text>
            </g>
          );
        })}
        {axes.map((label, i) => {
          const lx   = px(i, R * 1.25);
          const ly   = py(i, R * 1.25);
          const cosA = Math.cos(angle(i));
          const anchor = Math.abs(cosA) < 0.15 ? 'middle' : (cosA > 0 ? 'start' : 'end');
          return (
            <text
              key={i} x={lx} y={ly + 4}
              fontSize={11} fontWeight={600} fill={T.textSecondary} textAnchor={anchor} fontFamily="inherit"
              paintOrder="stroke" stroke={T.bgCardSolid} strokeWidth={5} strokeLinejoin="round"
            >{label}</text>
          );
        })}
      </svg>
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 10, top: tooltip.y - 30, zIndex: 4000,
          background: T.bgCardSolid, border: `1px solid ${T.accent}`,
          borderRadius: 8, padding: '4px 10px', fontSize: 12,
          color: T.textPrimary, pointerEvents: 'none', whiteSpace: 'nowrap',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {tooltip.label}: <strong>{tooltip.score}%</strong>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────── SubjectSection ─────────────────────────── */

function SubjectSection({ sub, lang, T, onWeaknessClick, radarData: radarDataProp }) {
  const name     = lang === 'ar' ? sub.name : sub.name_en;
  const color    = scoreColor(sub.score);
  const radarData = radarDataProp ?? SUBJECT_RADAR[sub.name];
  const strongs  = lang === 'ar' ? sub.strong_points_ar : sub.strong_points_en;
  const rec      = lang === 'ar' ? sub.rec_ar : sub.rec_en;

  const correctPct = sub.total_questions > 0
    ? Math.round((sub.correct_answers / sub.total_questions) * 100)
    : 0;

  const miniStat = (value, label) => (
    <div style={{ textAlign: 'center', minWidth: 60 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        background: 'rgba(150,150,150,0.05)', borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap', gap: 10,
      }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: T.textPrimary }}>
          {sub.emoji} {name}
        </div>
        <span style={{ padding: '4px 14px', background: `${color}22`, color, borderRadius: 20, fontSize: 14, fontWeight: 800 }}>
          {sub.score}%
        </span>
      </div>

      <div style={{ padding: '12px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 20, justifyContent: 'space-around', flexWrap: 'wrap' }}>
        {miniStat(sub.sessions, lang === 'ar' ? 'الجلسات' : 'Sessions')}
        {miniStat(`${correctPct}%`, lang === 'ar' ? 'إجابات صحيحة' : 'Correct')}
        {miniStat(sub.weaknesses.length, lang === 'ar' ? 'نقاط الضعف' : 'Weaknesses')}
        {miniStat(formatShortDate(sub.last_session, lang), lang === 'ar' ? 'آخر جلسة' : 'Last Session')}
      </div>

      <div style={{ padding: '14px 18px', display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {radarData && (
          <div style={{ flex: '0 0 auto', paddingInlineEnd: 56 }}>
            <RadarChart radarData={radarData} T={T} lang={lang} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
          {strongs.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#22c55e', marginBottom: 6 }}>
                {lang === 'ar' ? 'نقاط القوة' : 'Strengths'}
              </div>
              {strongs.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4, fontSize: 13, color: T.textPrimary }}>
                  <span style={{ color: '#22c55e', fontWeight: 700, flexShrink: 0 }}>●</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          )}
          {sub.weaknesses.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', marginBottom: 6 }}>
                {lang === 'ar' ? 'نقاط الضعف' : 'Weaknesses'}
              </div>
              {sub.weaknesses.map((w, i) => {
                const wName = lang === 'ar' ? w.name : (w.name_en || w.name);
                return (
                  <div
                    key={i}
                    onClick={e => { e.stopPropagation(); onWeaknessClick({ weakness: w.name, subjectName: name, x: e.clientX, y: e.clientY }); }}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4, fontSize: 13, cursor: 'pointer' }}
                    title={lang === 'ar' ? 'انقر للتفاصيل' : 'Click for details'}
                  >
                    <span style={{ color: '#ef4444', fontWeight: 700, flexShrink: 0 }}>●</span>
                    <span style={{ color: '#ef4444', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>{wName}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {rec && (
        <div style={{
          margin: '0 18px 14px', padding: '10px 14px',
          background: 'rgba(245,158,11,0.1)', borderRadius: 10,
          borderInlineStart: '4px solid #f59e0b', fontSize: 13,
          color: T.textPrimary, lineHeight: 1.6,
        }}>
          {rec}
        </div>
      )}
    </div>
  );
}

/* ───────────────────────────── DummyReportModal ─────────────────────── */

/* ──────────────────────────── action plan constants ───────────────── */

const PRIORITY_ORDER = ['high', 'medium', 'low'];

const PRIORITY_COLOR = { high: '#ef4444', medium: '#f59e0b', low: '#10b981' };

const ACTION_TYPE_COLOR = {
  home_activity:       '#10b981',
  conversation:        '#059669',
  observation:         '#8b5cf6',
  reinforcement:       '#f59e0b',
  review:              '#059669',
  restructure:         '#8b5cf6',
  increase_difficulty: '#ef4444',
  visual_explanation:  '#10b981',
  targeted_practice:   '#f59e0b',
};

function ActionPlanSection({ actionPlan, rt, reportLang }) {
  const { T } = useTheme();
  const lang  = reportLang;
  const items = actionPlan?.action_plan || [];

  if (!actionPlan || items.length === 0) {
    return (
      <div style={{ padding: '14px 18px', background: 'rgba(150,150,150,0.06)', border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 14, color: T.textSecondary, fontStyle: 'italic' }}>
        {rt.actionPlanEmpty}
      </div>
    );
  }

  const sorted  = [...items].sort((a, b) => PRIORITY_ORDER.indexOf(a.priority) - PRIORITY_ORDER.indexOf(b.priority));
  const grouped = { high: [], medium: [], low: [] };
  sorted.forEach(item => { (grouped[item.priority] ?? grouped.low).push(item); });

  const priorityHeaders = {
    high:   { label: rt.priorityHigh,   color: PRIORITY_COLOR.high   },
    medium: { label: rt.priorityMedium, color: PRIORITY_COLOR.medium },
    low:    { label: rt.priorityLow,    color: PRIORITY_COLOR.low    },
  };

  const typeBadge = (type) => {
    const color = ACTION_TYPE_COLOR[type] || T.textSecondary;
    const label = rt.actionTypes?.[type] || type;
    return (
      <span style={{ fontSize: 11, fontWeight: 600, color, background: `${color}18`, borderRadius: 20, padding: '2px 8px', marginTop: 4, display: 'inline-block' }}>
        {label}
      </span>
    );
  };

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginBottom: 16 }}>
        {rt.actionPlanTitle}
      </div>
      {PRIORITY_ORDER.map(priority => {
        const group = grouped[priority] || [];
        if (group.length === 0) return null;
        const ph = priorityHeaders[priority];
        return (
          <div key={priority}>
            <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.5px', color: ph.color, marginTop: 20, marginBottom: 10 }}>
              {ph.label}
            </div>
            {group.map((item, idx) => {
              const pColor = PRIORITY_COLOR[item.priority] || PRIORITY_COLOR.low;
              return (
                <div key={idx} style={{ background: 'rgba(150,150,150,0.04)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '18px 20px', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 6 }}>
                    {[item.subject, item.unit, item.topic].filter(Boolean).join(' › ')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ background: `${pColor}18`, color: pColor, fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
                      {item.weakness_name}
                    </span>
                    {(item.occurrence_count ?? 0) > 0 && (
                      <span style={{ fontSize: 12, color: T.textSecondary }}>
                        {rt.occurrenceLabel} {item.occurrence_count} {rt.occurrenceSuffix}
                      </span>
                    )}
                  </div>
                  {item.exam_note && (
                    <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: T.textPrimary, marginBottom: 10, marginTop: 8 }}>
                      ⚠️ {item.exam_note}
                    </div>
                  )}
                  {item.guardian_block && (
                    <div style={{ background: 'rgba(5,150,105,0.05)', borderInlineStart: '3px solid #059669', borderRadius: 8, padding: '14px 16px', marginTop: 10, marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#059669', marginBottom: 10 }}>
                        🧑‍👦 {rt.guardianBlockLabel}
                      </div>
                      {item.guardian_block.opening && (
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.accent, marginBottom: 6 }}>
                          {item.guardian_block.opening}
                        </div>
                      )}
                      {item.guardian_block.educational_context && (
                        <div style={{ fontSize: 13, fontStyle: 'italic', color: T.textSecondary, marginBottom: 12 }}>
                          {item.guardian_block.educational_context}
                        </div>
                      )}
                      {(item.guardian_block.actions || []).map((action, ai) => (
                        <div key={ai} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                          <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#059669', color: 'white', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                            {ai + 1}
                          </span>
                          <div>
                            <div style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{action.action_text}</div>
                            {typeBadge(action.action_type)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {item.jahbath_block && (
                    <div style={{ background: 'rgba(5,150,105,0.06)', borderInlineStart: '3px solid #10b981', borderRadius: 8, padding: '14px 16px', marginTop: 10, marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginBottom: 10 }}>
                        🤖 {rt.jahbathBlockLabel}
                      </div>
                      {(item.jahbath_block.actions || []).map((action, ai) => (
                        <div key={ai} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', flexShrink: 0, marginTop: 8 }} />
                          <div>
                            <div style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{action.action_text}</div>
                            {typeBadge(action.action_type)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function DummyReportModal({ report, onClose, onResend, showToast, lang }) {
  const { T } = useTheme();
  const rt = reportTranslations[lang] || reportTranslations['ar'];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const badge = TYPE_BADGE[report.report_type] || { label: { ar: report.report_type, en: report.report_type }, color: T.textSecondary, bg: T.bgCardSolid };
  const studentName = lang === 'ar' ? report.student_name : (report.student_name_en || report.student_name);
  const school = lang === 'ar' ? report.school : (report.school_en || report.school);
  const correctPct = report.total_questions > 0 ? Math.round((report.correct_answers / report.total_questions) * 100) : 0;
  const [weaknessPopup, setWeaknessPopup] = useState(null);

  const sectionTitle = (text) => (
    <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, borderInlineStart: `4px solid ${T.accent}`, paddingInlineStart: 10, marginBottom: 14, marginTop: 6 }}>
      {text}
    </div>
  );

  const statBox = (value, label, color) => (
    <div style={{ flex: 1, minWidth: 90, background: 'rgba(150,150,150,0.07)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || T.accent, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>{label}</div>
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) { setWeaknessPopup(null); onClose(); } }}
    >
      <style>{`
        @keyframes wr-fadein { from { opacity:0; transform:scale(0.94) } to { opacity:1; transform:scale(1) } }
        @media print { .no-print { display:none!important } .print-modal { box-shadow:none!important; border:none!important; max-height:none!important } }
      `}</style>

      <div className="print-modal" style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: dir }}>

        <div style={{ padding: '20px 26px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0, background: 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, transparent 60%)' }}>
          <div>
            <span style={{ display: 'inline-block', padding: '3px 12px', background: badge.bg, color: badge.color, borderRadius: 20, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{badge.label[lang]}</span>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.textPrimary, marginBottom: 2 }}>{studentName}</div>
            <div style={{ fontSize: 13, color: T.textSecondary }}>{lang === 'ar' ? `الصف ${report.grade} — ` : `Grade ${report.grade} — `}{school}</div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{formatDate(report.created_at, lang)}</div>
          </div>
          <button className="no-print" style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '40px 48px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 26 }}>

          <div>
            {sectionTitle(rt.executiveSummary)}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {statBox(`${report.avg_score}%`, lang === 'ar' ? 'متوسط الدرجة' : 'Avg Score', scoreColor(report.avg_score))}
              {statBox(report.sessions_this_month, lang === 'ar' ? 'جلسات هذا الشهر' : 'Sessions', T.accent)}
              {statBox(report.total_questions, lang === 'ar' ? 'إجمالي الأسئلة' : 'Questions', '#8b5cf6')}
              {statBox(`${correctPct}%`, lang === 'ar' ? 'نسبة الصحيحة' : 'Correct', scoreColor(correctPct))}
              {statBox(report.weaknesses_count, lang === 'ar' ? 'نقاط الضعف' : 'Weaknesses', report.weaknesses_count > 0 ? '#f59e0b' : '#22c55e')}
            </div>
          </div>

          <div>
            {sectionTitle(rt.performanceBySubject)}
            {(() => {
              const cambridge = report.subjects.filter(s => s.curriculum !== 'qatar');
              const qatar     = report.subjects.filter(s => s.curriculum === 'qatar');
              const subList = (arr) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {arr.map((sub, i) => <SubjectSection key={i} sub={sub} lang={lang} T={T} onWeaknessClick={setWeaknessPopup} />)}
                </div>
              );
              return (
                <>
                  {cambridge.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 12, paddingInlineStart: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {rt.cambridgeSection}
                      </div>
                      {subList(cambridge)}
                    </div>
                  )}
                  {qatar.length > 0 && (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {rt.qatarSection}
                        </div>
                        <div style={{ fontSize: 11, color: T.textSecondary, background: 'rgba(139,92,246,0.1)', borderRadius: 8, padding: '3px 8px' }}>
                          {lang === 'ar' ? 'تُقيَّم بمعايير مستقلة' : 'Independent'}
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12, fontStyle: 'italic', lineHeight: 1.5 }}>
                        {rt.qatarNote}
                      </div>
                      {subList(qatar)}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <div>
            {sectionTitle(rt.streakTitle)}
            <div style={{ marginBottom: 12, fontSize: 13, color: T.textSecondary }}>
              {lang === 'ar'
                ? `${report.streak_days} أيام متتالية · الأيام النشطة مُظللة باللون الأزرق`
                : `${report.streak_days} consecutive days · Active days highlighted in blue`}
            </div>
            <StreakCalendar activeDays={report.active_days} T={T} />
          </div>

          {report.recommendations && report.recommendations.length > 0 && (
            <div>
              {sectionTitle(rt.pedagogicalTitle)}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {report.recommendations.map((rec, i) => {
                  const rc = REC_COLORS[rec.type] || REC_COLORS.blue;
                  const text = lang === 'ar' ? rec.text_ar : rec.text_en;
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: rc.bg, borderRadius: 10, padding: '12px 14px', borderInlineStart: `4px solid ${rc.border}` }}>
                      <span style={{ fontSize: 16, color: rc.border, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{rc.icon}</span>
                      <span style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ActionPlanSection actionPlan={report.action_plan} rt={rt} reportLang={lang} />

        </div>

        <div className="no-print" style={{ padding: '14px 26px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <button
            style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => showToast(lang === 'ar' ? 'قريباً — ميزة تصدير PDF' : 'Coming soon — PDF export', 'info')}
          >
            {rt.exportPDF}
          </button>
          <button
            style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => showToast(lang === 'ar' ? 'التقارير الحقيقية فقط يمكن إعادة إرسالها' : 'Only real reports can be resent', 'info')}
          >
            {rt.sendEmail}
          </button>
          <button
            style={{ flex: 2, minWidth: 140, padding: '10px 0', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={onClose}
          >
            {rt.close}
          </button>
        </div>
      </div>

      {weaknessPopup && (
        <WeaknessPopup
          data={weaknessPopup}
          lang={lang}
          T={T}
          onClose={() => setWeaknessPopup(null)}
        />
      )}
    </div>
  );
}

/* ───────────────────────────────── SessionReportModal ──────────────── */

const CONCEPT_STATUS = {
  good:   { icon: '✅', label_ar: 'فهم جيد',       label_en: 'Good Understanding', color: '#22c55e' },
  review: { icon: '⚠️', label_ar: 'يحتاج مراجعة', label_en: 'Needs Review',       color: '#f59e0b' },
  fail:   { icon: '❌', label_ar: 'لم يفهم',        label_en: 'Not Understood',     color: '#ef4444' },
};

function SessionRadarChart({ radarData, T, lang }) {
  const [tooltip, setTooltip] = useState(null);
  const N = 5;
  const cx = 140, cy = 140, R = 100;
  const axes   = lang === 'ar' ? radarData.axes_ar : radarData.axes_en;
  const scores = radarData.scores;
  const angle  = i => (2 * Math.PI / N) * i - Math.PI / 2;
  const px     = (i, r) => cx + r * Math.cos(angle(i));
  const py     = (i, r) => cy + r * Math.sin(angle(i));
  const polyPts = r => Array.from({ length: N }, (_, i) => `${px(i, r)},${py(i, r)}`).join(' ');
  const activeScores = scores.map(s => Math.max(s, 0));
  const dataPts = activeScores.map((s, i) => `${px(i, R * s / 100)},${py(i, R * s / 100)}`).join(' ');

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
      <svg width="280" height="280" viewBox="0 0 280 280" style={{ maxWidth: '100%', overflow: 'visible' }}>
        {[0.25, 0.5, 0.75, 1].map((lvl, li) => (
          <polygon key={li} points={polyPts(R * lvl)} fill="none" stroke={T.border} strokeOpacity={0.45} strokeWidth={1} />
        ))}
        {Array.from({ length: N }, (_, i) => (
          <line key={i} x1={cx} y1={cy} x2={px(i, R)} y2={py(i, R)} stroke={T.border} strokeOpacity={0.3} strokeWidth={1} />
        ))}
        <polygon points={dataPts} fill={T.accent} fillOpacity={0.18} stroke={T.accent} strokeWidth={2} />
        {scores.map((s, i) => {
          const active = s > 0;
          const x    = px(i, R * (active ? s : 0) / 100);
          const y    = py(i, R * (active ? s : 0) / 100);
          const cosA = Math.cos(angle(i));
          const sinA = Math.sin(angle(i));
          const anchor = Math.abs(cosA) < 0.15 ? 'middle' : (cosA > 0 ? 'start' : 'end');
          const lx = px(i, R * 1.25);
          const ly = py(i, R * 1.25);
          return (
            <g key={i}>
              {active && (
                <circle
                  cx={x} cy={y} r={5} fill={T.accent} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => setTooltip({ x: e.clientX, y: e.clientY, label: axes[i], score: s })}
                  onMouseLeave={() => setTooltip(null)}
                />
              )}
              {active && (
                <text
                  x={x + 8 * cosA} y={y + 8 * sinA + 3}
                  fontSize={10} textAnchor={anchor} fontFamily="inherit" fill={T.textSecondary}
                  paintOrder="stroke" stroke={T.bgCardSolid} strokeWidth={4} strokeLinejoin="round"
                >{s}%</text>
              )}
              <text
                x={lx} y={ly + 4}
                fontSize={11} fontWeight={600} textAnchor={anchor} fontFamily="inherit"
                fill={active ? T.textSecondary : 'rgba(150,150,150,0.45)'}
                paintOrder="stroke" stroke={T.bgCardSolid} strokeWidth={5} strokeLinejoin="round"
              >{axes[i]}{!active ? ' *' : ''}</text>
            </g>
          );
        })}
      </svg>
      {tooltip && (
        <div style={{ position: 'fixed', left: tooltip.x + 10, top: tooltip.y - 30, zIndex: 4000, background: T.bgCardSolid, border: `1px solid ${T.accent}`, borderRadius: 8, padding: '4px 10px', fontSize: 12, color: T.textPrimary, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
          {tooltip.label}: <strong>{tooltip.score}%</strong>
        </div>
      )}
    </div>
  );
}

function SessionReportModal({ report, onClose, onResend, showToast, lang }) {
  const { T } = useTheme();
  const rt = reportTranslations[lang] || reportTranslations['ar'];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const studentName  = lang === 'ar' ? report.student_name : (report.student_name_en || report.student_name);
  const subject      = lang === 'ar' ? report.subject_ar  : report.subject_en;
  const unit         = lang === 'ar' ? report.unit_ar     : report.unit_en;
  const topic        = lang === 'ar' ? report.topic_ar    : report.topic_en;
  const summaryText  = lang === 'ar' ? report.summary_ar  : report.summary_en;
  const nextTopic    = lang === 'ar' ? report.next_topic_ar : report.next_topic_en;
  const nextRec      = lang === 'ar' ? report.next_rec_ar : report.next_rec_en;
  const [weaknessPopup, setWeaknessPopup] = useState(null);

  const correctCount = report.questions.filter(q => q.correct).length;
  const totalCount   = report.questions.length;
  const scorePct     = Math.round((correctCount / totalCount) * 100);

  const sectionTitle = (text) => (
    <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, borderInlineStart: `4px solid ${T.accent}`, paddingInlineStart: 10, marginBottom: 14, marginTop: 6 }}>
      {text}
    </div>
  );

  const formatSessionDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const datePart = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
      const timePart = new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA' : 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
      return `${datePart} | ${timePart}`;
    } catch { return iso; }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) { setWeaknessPopup(null); onClose(); } }}
    >
      <style>{`@keyframes wr-fadein{from{opacity:0;transform:scale(0.94)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: dir }}>

        <div style={{ padding: '20px 26px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0, background: 'linear-gradient(135deg, rgba(6,182,212,0.08) 0%, transparent 60%)' }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ padding: '3px 12px', background: 'rgba(6,182,212,0.15)', color: '#06b6d4', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                {lang === 'ar' ? 'تقرير جلسة' : 'Session Report'}
              </span>
              <span style={{ padding: '3px 12px', background: 'rgba(150,150,150,0.1)', color: T.textSecondary, borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                {lang === 'ar' ? `مدة الجلسة: ${report.duration_mins} دقيقة` : `Session: ${report.duration_mins} min`}
              </span>
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, color: T.textPrimary, marginBottom: 2 }}>
              {lang === 'ar' ? `تقرير جلسة — ${subject}` : `Session Report — ${subject}`}
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 2 }}>
              {lang === 'ar' ? `الطالب: ${studentName} | الصف الرابع` : `Student: ${studentName} | Grade ${report.grade}`}
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary }}>{formatSessionDate(report.created_at)}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '40px 48px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 26 }}>

          <div>
            {sectionTitle(rt.sessionSummary)}
            <div style={{ background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                {[
                  { label: lang === 'ar' ? 'المادة'    : 'Subject',  value: subject },
                  { label: lang === 'ar' ? 'الوحدة'    : 'Unit',     value: unit    },
                  { label: lang === 'ar' ? 'الموضوع'   : 'Topic',    value: topic   },
                  { label: lang === 'ar' ? 'حالة الجلسة' : 'Status', value: lang === 'ar' ? '✅ مكتملة' : '✅ Completed' },
                ].map((item, i) => (
                  <div key={i} style={{ flex: 1, minWidth: 110, background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.75, margin: 0 }}>{summaryText}</p>
            </div>
          </div>

          <div>
            {sectionTitle(rt.conceptsCovered)}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {report.concepts.map((c, i) => {
                const st   = CONCEPT_STATUS[c.status] || CONCEPT_STATUS.good;
                const name = lang === 'ar' ? c.name_ar : c.name_en;
                const stLabel = lang === 'ar' ? st.label_ar : st.label_en;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(150,150,150,0.05)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '11px 14px', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 16 }}>{st.icon}</span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 12, color: st.color, fontWeight: 600 }}>{stLabel}</span>
                      <span style={{ fontSize: 12, color: T.textSecondary }}>
                        {lang === 'ar' ? `${c.q_total} أسئلة تحقق` : `${c.q_total} check questions`}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(Math.round(c.q_correct / c.q_total * 100)), background: `${scoreColor(Math.round(c.q_correct / c.q_total * 100))}18`, borderRadius: 20, padding: '3px 10px' }}>
                        {c.q_correct}/{c.q_total}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            {sectionTitle(rt.questionsDetail)}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(150,150,150,0.08)' }}>
                    {[
                      lang === 'ar' ? 'السؤال'          : 'Question',
                      lang === 'ar' ? `إجابة ${report.student_name}` : `${report.student_name_en || report.student_name}'s Answer`,
                      lang === 'ar' ? 'الإجابة الصحيحة' : 'Correct Answer',
                      lang === 'ar' ? 'النتيجة'          : 'Result',
                    ].map((h, i) => (
                      <th key={i} style={{ padding: '9px 12px', fontWeight: 700, color: T.textSecondary, textAlign: lang === 'ar' ? 'right' : 'left', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.questions.map((q, i) => {
                    const qText  = lang === 'ar' ? q.q_ar       : q.q_en;
                    const stuAns = lang === 'ar' ? q.student_ar  : q.student_en;
                    const corAns = lang === 'ar' ? q.correct_ar  : q.correct_en;
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${T.border}`, background: q.correct ? 'transparent' : 'rgba(239,68,68,0.04)' }}>
                        <td style={{ padding: '10px 12px', color: T.textPrimary }}>{qText}</td>
                        <td style={{ padding: '10px 12px', color: q.correct ? T.textSecondary : '#ef4444', fontWeight: q.correct ? 400 : 600 }}>{stuAns}</td>
                        <td style={{ padding: '10px 12px', color: T.textSecondary }}>{corAns}</td>
                        <td style={{ padding: '10px 12px', fontSize: 18, textAlign: 'center' }}>{q.correct ? '✅' : '❌'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>
                {lang === 'ar' ? `الإجمالي: ${correctCount}/${totalCount} — ${scorePct}%` : `Total: ${correctCount}/${totalCount} — ${scorePct}%`}
              </span>
              <div style={{ flex: 1, minWidth: 120 }}>
                <ProgressBar value={scorePct} color={scoreColor(scorePct)} />
              </div>
            </div>
          </div>

          <div>
            {sectionTitle(lang === 'ar' ? 'الأداء لكل محور في هذه الجلسة' : 'Session Performance by Axis')}
            <SessionRadarChart radarData={report.session_radar} T={T} lang={lang} />
            <div style={{ marginTop: 10, fontSize: 12, color: T.textSecondary, fontStyle: 'italic', textAlign: 'center' }}>
              {lang === 'ar'
                ? '* تغطي هذه الجلسة محوري الأعداد والجبر فقط — المحاور الرمادية لم تُدرَّس بعد'
                : '* This session covers Numbers and Algebra only — grayed axes not yet taught'}
            </div>
          </div>

          <div>
            {sectionTitle(rt.weaknessesDetected)}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              {report.session_weaknesses.map((w, i) => {
                const wName = lang === 'ar' ? w.name : (w.name_en || w.name);
                return (
                  <span
                    key={i}
                    onClick={e => { e.stopPropagation(); setWeaknessPopup({ weakness: w.name, subjectName: subject, x: e.clientX, y: e.clientY }); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, fontSize: 13, color: '#ef4444', cursor: 'pointer', userSelect: 'none' }}
                    title={lang === 'ar' ? 'انقر للتفاصيل' : 'Click for details'}
                  >
                    <span style={{ fontWeight: 700 }}>●</span> {wName}
                  </span>
                );
              })}
            </div>
            <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, fontSize: 13, color: T.textPrimary, lineHeight: 1.6 }}>
              {rt.autoSavedNote}
            </div>
          </div>

          <div>
            {sectionTitle(rt.nextSession)}
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: 'rgba(5,150,105,0.08)', borderRadius: 12, padding: '14px 16px', borderInlineStart: '4px solid #059669', marginBottom: 16 }}>
              <span style={{ fontSize: 18, color: '#059669', fontWeight: 800, flexShrink: 0 }}>→</span>
              <span style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.75 }}>{nextRec}</span>
            </div>
            <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 8 }}>
              {lang === 'ar'
                ? `جاهزية ${report.student_name} للموضوع التالي (${nextTopic}): ${report.next_topic_readiness}%`
                : `${report.student_name_en || report.student_name}'s readiness for next topic (${nextTopic}): ${report.next_topic_readiness}%`}
            </div>
            <ProgressBar value={report.next_topic_readiness} color={scoreColor(report.next_topic_readiness)} />
          </div>

          <ActionPlanSection actionPlan={report.action_plan} rt={rt} reportLang={lang} />

        </div>

        <div style={{ padding: '14px 26px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <button style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => showToast(lang === 'ar' ? 'قريباً — ميزة تصدير PDF' : 'Coming soon — PDF export', 'info')}>
            {rt.exportPDF}
          </button>
          <button style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => showToast(lang === 'ar' ? 'التقارير الحقيقية فقط يمكن إعادة إرسالها' : 'Only real reports can be resent', 'info')}>
            {rt.sendEmail}
          </button>
          <button style={{ flex: 2, minWidth: 140, padding: '10px 0', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={onClose}>
            {rt.close}
          </button>
        </div>

      </div>

      {weaknessPopup && (
        <WeaknessPopup data={weaknessPopup} lang={lang} T={T} onClose={() => setWeaknessPopup(null)} />
      )}
    </div>
  );
}

/* ─────────────────────────────── QuizReportModal ───────────────────── */

const DIFF_BADGE = {
  easy:   { label_ar: 'سهل',   label_en: 'Easy',   color: '#22c55e', bg: 'rgba(34,197,94,0.15)'   },
  medium: { label_ar: 'متوسط', label_en: 'Medium', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)'  },
  hard:   { label_ar: 'صعب',   label_en: 'Hard',   color: '#ef4444', bg: 'rgba(239,68,68,0.15)'   },
};

function ScoreCircle({ score, T }) {
  const [animated, setAnimated] = useState(0);
  const R = 54, C = 2 * Math.PI * R;
  useEffect(() => {
    const t = setTimeout(() => setAnimated(score), 120);
    return () => clearTimeout(t);
  }, [score]);
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  const offset = C - (animated / 100) * C;
  return (
    <svg width={130} height={130} style={{ display: 'block', margin: '0 auto' }}>
      <circle cx={65} cy={65} r={R} fill="none" stroke="rgba(150,150,150,0.18)" strokeWidth={10} />
      <circle
        cx={65} cy={65} r={R} fill="none"
        stroke={color} strokeWidth={10}
        strokeDasharray={C} strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 65 65)"
        style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)' }}
      />
      <text x={65} y={62} textAnchor="middle" fontSize={26} fontWeight={700} fill={color}>{score}</text>
      <text x={65} y={80} textAnchor="middle" fontSize={12} fill={T.textSecondary}>%</text>
    </svg>
  );
}

function QuizReportModal({ report, onClose, showToast, lang }) {
  const { T } = useTheme();
  const rt = reportTranslations[lang] || reportTranslations['ar'];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const [filterTab, setFilterTab] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const [weakness, setWeakness] = useState(null);

  const questions = report.quiz_questions || [];
  const filtered = filterTab === 'all' ? questions
    : filterTab === 'correct' ? questions.filter(q => q.correct)
    : questions.filter(q => !q.correct);
  const visible = showAll ? filtered : filtered.slice(0, 8);
  const remaining = filtered.length - 8;

  const totalPrev = report.axis_comparison.reduce((s, a) => s + a.prev, 0);
  const totalCurr = report.axis_comparison.reduce((s, a) => s + a.curr, 0);
  const totalChange = totalCurr - totalPrev;

  const correctCount  = questions.filter(q =>  q.correct).length;
  const wrongCount    = questions.filter(q => !q.correct).length;

  const badge = TYPE_BADGE.quiz_report;

  const sectionBox = (children) => (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 22px', marginBottom: 18 }}>
      {children}
    </div>
  );
  const sectionTitle = (text) => (
    <div style={{ fontWeight: 700, fontSize: 15, color: T.textPrimary, marginBottom: 14, borderBottom: `1.5px solid ${T.border}`, paddingBottom: 8 }}>
      {text}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: T.bgCardSolid, borderRadius: 18, width: '100%', maxWidth: 920,
        maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.38)',
        direction: dir,
      }}>
        {weakness && (
          <WeaknessPopup
            data={weakness}
            lang={lang}
            T={T}
            onClose={() => setWeakness(null)}
          />
        )}

        {/* ── Header ── */}
        <div style={{ padding: '22px 26px 18px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ background: badge.bg, color: badge.color, border: `1px solid ${badge.color}`, borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
                {lang === 'ar' ? badge.label.ar : badge.label.en}
              </span>
              <span style={{ fontWeight: 700, fontSize: 16, color: T.textPrimary }}>
                {lang === 'ar' ? `اختبار — ${report.subject_ar}` : `Quiz — ${report.subject_en}`}
              </span>
              <span style={{ background: 'rgba(150,150,150,0.12)', color: T.textSecondary, borderRadius: 20, padding: '2px 10px', fontSize: 12, border: `1px solid ${T.border}` }}>
                {lang === 'ar' ? report.quiz_type_ar : report.quiz_type_en}
              </span>
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary }}>
              {lang === 'ar'
                ? `${report.student_name} · الصف ${report.grade} · ${formatDate(report.created_at, 'ar')}`
                : `${report.student_name_en} · Grade ${report.grade} · ${formatDate(report.created_at, 'en')}`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(150,150,150,0.12)', border: 'none', borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: T.textSecondary, fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ padding: '40px 48px' }}>

          {/* ── S1: Quiz Overview ── */}
          {sectionBox(<>
            {sectionTitle(rt.quizOverview)}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
              {[
                { label_ar: 'إجمالي الأسئلة', label_en: 'Total Questions', value: report.total_questions, color: T.accent },
                { label_ar: 'إجابات صحيحة',   label_en: 'Correct',         value: correctCount,           color: '#22c55e' },
                { label_ar: 'إجابات خاطئة',   label_en: 'Wrong',           value: wrongCount,             color: '#ef4444' },
                { label_ar: 'النتيجة السابقة', label_en: 'Previous Score',  value: `${report.prev_score}%`,color: '#f59e0b' },
              ].map((s, i) => (
                <div key={i} style={{ flex: '1 1 120px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{lang === 'ar' ? s.label_ar : s.label_en}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
              <ScoreCircle score={report.score} T={T} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: T.textPrimary, marginBottom: 10 }}>
                  {lang === 'ar' ? 'مقارنة بالاختبار السابق' : 'Comparison with Previous Quiz'}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{report.prev_score}%</div>
                    <div style={{ fontSize: 11, color: T.textSecondary }}>{lang === 'ar' ? 'السابق' : 'Previous'}</div>
                  </div>
                  <div style={{ fontSize: 20, color: T.textSecondary }}>→</div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: scoreColor(report.score) }}>{report.score}%</div>
                    <div style={{ fontSize: 11, color: T.textSecondary }}>{lang === 'ar' ? 'الحالي' : 'Current'}</div>
                  </div>
                  <div style={{ marginInlineStart: 8, fontWeight: 700, fontSize: 16, color: report.score >= report.prev_score ? '#22c55e' : '#ef4444' }}>
                    {report.score >= report.prev_score ? '▲' : '▼'} {Math.abs(report.score - report.prev_score)}%
                  </div>
                </div>
              </div>
            </div>
          </>)}

          {/* ── S2: Performance by Axis ── */}
          {sectionBox(<>
            {sectionTitle(rt.axisPerformance)}
            <div style={{ display: 'flex', gap: 0, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div style={{ flex: '0 0 auto', paddingInlineEnd: 56 }}>
                <RadarChart radarData={report.quiz_radar} T={T} lang={lang} />
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                {report.quiz_radar.scores.map((score, i) => (
                  <div key={i} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: T.textSecondary, width: 140, flexShrink: 0 }}>
                      {lang === 'ar' ? report.quiz_radar.axes_ar[i] : report.quiz_radar.axes_en[i]}
                    </span>
                    <ProgressBar value={score} color={scoreColor(score)} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(score), minWidth: 36, textAlign: 'start' }}>{score}%</span>
                  </div>
                ))}
              </div>
            </div>
          </>)}

          {/* ── S3: Question Breakdown ── */}
          {sectionBox(<>
            {sectionTitle(rt.questionBreakdown)}

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                { key: 'all',     label_ar: `الكل (${questions.length})`,          label_en: `All (${questions.length})`           },
                { key: 'correct', label_ar: `✅ صحيح (${correctCount})`,           label_en: `✅ Correct (${correctCount})`         },
                { key: 'wrong',   label_ar: `❌ خطأ (${wrongCount})`,              label_en: `❌ Wrong (${wrongCount})`            },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setFilterTab(tab.key); setShowAll(false); }}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer', fontWeight: filterTab === tab.key ? 700 : 400,
                    background: filterTab === tab.key ? T.accent : 'rgba(150,150,150,0.1)',
                    color: filterTab === tab.key ? '#fff' : T.textSecondary,
                    border: filterTab === tab.key ? 'none' : `1px solid ${T.border}`,
                  }}
                >
                  {lang === 'ar' ? tab.label_ar : tab.label_en}
                </button>
              ))}
            </div>

            {/* Questions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {visible.map((q) => {
                const db = DIFF_BADGE[q.diff] || DIFF_BADGE.medium;
                return (
                  <div key={q.n} style={{
                    background: q.correct ? T.bgCard : 'rgba(239,68,68,0.06)',
                    border: `1px solid ${q.correct ? T.border : 'rgba(239,68,68,0.25)'}`,
                    borderRadius: 12, padding: '12px 14px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 18, lineHeight: 1 }}>{q.correct ? '✅' : '❌'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary }}>
                          {lang === 'ar' ? q.q_ar : q.q_en}
                        </div>
                      </div>
                      <span style={{ background: db.bg, color: db.color, borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 600, flexShrink: 0, border: `1px solid ${db.color}` }}>
                        {lang === 'ar' ? db.label_ar : db.label_en}
                      </span>
                      <span style={{ background: 'rgba(150,150,150,0.1)', color: T.textSecondary, borderRadius: 20, padding: '2px 9px', fontSize: 11, flexShrink: 0, border: `1px solid ${T.border}` }}>
                        {lang === 'ar' ? q.axis_ar : q.axis_en}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: T.textSecondary, paddingInlineStart: 28 }}>
                      <span style={{ marginInlineEnd: 4 }}>{lang === 'ar' ? 'إجابة الطالب:' : 'Student:'}</span>
                      <span style={{ color: q.correct ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {lang === 'ar' ? q.student_ar : q.student_en}
                      </span>
                      {!q.correct && (
                        <>
                          <span style={{ margin: '0 6px', color: T.textSecondary }}>·</span>
                          <span style={{ marginInlineEnd: 4 }}>{lang === 'ar' ? 'الصحيح:' : 'Correct:'}</span>
                          <span style={{ color: '#22c55e', fontWeight: 600 }}>
                            {lang === 'ar' ? q.answer_ar : q.answer_en}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Show more / collapse */}
            {filtered.length > 8 && (
              <button
                onClick={() => setShowAll(v => !v)}
                style={{
                  marginTop: 12, width: '100%', padding: '9px 0', borderRadius: 10, cursor: 'pointer',
                  background: 'rgba(150,150,150,0.1)', border: `1px solid ${T.border}`,
                  color: T.textSecondary, fontSize: 13, fontWeight: 600,
                }}
              >
                {showAll
                  ? (lang === 'ar' ? '▲ إخفاء' : '▲ Collapse')
                  : (lang === 'ar' ? `▼ عرض باقي ${remaining} سؤالاً` : `▼ Show ${remaining} more questions`)}
              </button>
            )}
          </>)}

          {/* ── S4: Weaknesses ── */}
          {sectionBox(<>
            {sectionTitle(lang === 'ar' ? 'نقاط الضعف المرصودة' : 'Observed Weaknesses')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(report.quiz_weaknesses || []).map((w, i) => (
                <button
                  key={i}
                  onClick={e => setWeakness({ weakness: w.name, subjectName: lang === 'ar' ? report.subject_ar : report.subject_en, x: e.clientX, y: e.clientY })}
                  style={{
                    background: 'rgba(239,68,68,0.1)', color: '#ef4444',
                    border: '1.5px solid rgba(239,68,68,0.35)', borderRadius: 20,
                    padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ⚠ {lang === 'ar' ? w.name : w.name_en}
                </button>
              ))}
            </div>
          </>)}

          {/* ── S5: Axis Comparison Table ── */}
          {sectionBox(<>
            {sectionTitle(lang === 'ar' ? 'مقارنة المحاور بالاختبار السابق' : 'Axis Comparison vs Previous Quiz')}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'rgba(150,150,150,0.08)' }}>
                    {[
                      lang === 'ar' ? 'المحور' : 'Axis',
                      lang === 'ar' ? 'السابق' : 'Prev',
                      lang === 'ar' ? 'الحالي' : 'Curr',
                      lang === 'ar' ? 'التغيير' : 'Change',
                    ].map((h, i) => (
                      <th key={i} style={{ padding: '9px 12px', textAlign: i === 0 ? 'start' : 'center', color: T.textSecondary, fontWeight: 600, borderBottom: `1.5px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.axis_comparison.map((row, i) => {
                    const diff = row.curr - row.prev;
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '9px 12px', color: T.textPrimary, fontWeight: 600 }}>{lang === 'ar' ? row.axis_ar : row.axis_en}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', color: T.textSecondary }}>{row.prev}%</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', color: scoreColor(row.curr), fontWeight: 700 }}>{row.curr}%</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: diff >= 0 ? '#22c55e' : '#ef4444' }}>
                          {diff >= 0 ? '▲' : '▼'} {Math.abs(diff)}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total row */}
                  <tr style={{ background: 'rgba(150,150,150,0.06)', fontWeight: 700 }}>
                    <td style={{ padding: '9px 12px', color: T.textPrimary }}>{lang === 'ar' ? 'الإجمالي (مجموع)' : 'Total (sum)'}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center', color: T.textSecondary }}>{totalPrev}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center', color: scoreColor(totalCurr / report.axis_comparison.length), fontWeight: 700 }}>{totalCurr}</td>
                    <td style={{ padding: '9px 12px', textAlign: 'center', fontWeight: 700, color: totalChange >= 0 ? '#22c55e' : '#ef4444' }}>
                      {totalChange >= 0 ? '▲' : '▼'} {Math.abs(totalChange)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 10, padding: '9px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, fontSize: 12, color: '#f59e0b' }}>
              ⚠ {lang === 'ar'
                ? 'القيم تمثل النسب المئوية لكل محور. الإجمالي هو مجموع الأعمدة وليس معدلاً.'
                : 'Values represent percentage scores per axis. The total is a column sum, not an average.'}
            </div>
          </>)}

          {/* ── S6: Recommendations ── */}
          {sectionBox(<>
            {sectionTitle(lang === 'ar' ? 'توصيات جهبذ' : 'Jahbath Recommendations')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(report.quiz_recommendations || []).map((rec, i) => {
                const rc = REC_COLORS[rec.type] || REC_COLORS.blue;
                return (
                  <div key={i} style={{ background: rc.bg, border: `1.5px solid ${rc.border}`, borderRadius: 12, padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 16, color: rc.border, flexShrink: 0 }}>{rc.icon}</span>
                    <span style={{ fontSize: 13, color: T.textPrimary, lineHeight: 1.6 }}>{lang === 'ar' ? rec.text_ar : rec.text_en}</span>
                  </div>
                );
              })}
            </div>
          </>)}

          <ActionPlanSection actionPlan={report.action_plan} rt={rt} reportLang={lang} />

          {/* ── Footer ── */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button onClick={() => showToast(lang === 'ar' ? 'جارٍ تحضير ملف PDF...' : 'Preparing PDF...')} style={{ padding: '9px 20px', borderRadius: 10, cursor: 'pointer', background: 'rgba(150,150,150,0.12)', border: `1px solid ${T.border}`, color: T.textSecondary, fontSize: 13 }}>
              📄 {rt.exportPDF}
            </button>
            <button onClick={() => showToast(lang === 'ar' ? 'سيتم إرسال التقرير بالبريد الإلكتروني قريباً' : 'Report will be emailed shortly')} style={{ padding: '9px 20px', borderRadius: 10, cursor: 'pointer', background: 'rgba(150,150,150,0.12)', border: `1px solid ${T.border}`, color: T.textSecondary, fontSize: 13 }}>
              ✉ {rt.sendEmail}
            </button>
            <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 12, cursor: 'pointer', background: 'linear-gradient(135deg, #059669, #10B981)', border: 'none', color: '#fff', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 13, fontWeight: 700 }}>
              {rt.close}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────── WeeklyReportModal ───────────────── */

function WeeklyReportModal({ report, onClose, onResend, showToast, lang }) {
  const { T } = useTheme();
  const rt = reportTranslations[lang] || reportTranslations['ar'];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const studentName = lang === 'ar' ? report.student_name : (report.student_name_en || report.student_name);
  const weekLabel   = lang === 'ar' ? report.week_label_ar : report.week_label_en;
  const [weaknessPopup, setWeaknessPopup] = useState(null);

  const sectionTitle = (text) => (
    <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, borderInlineStart: `4px solid ${T.accent}`, paddingInlineStart: 10, marginBottom: 14, marginTop: 6 }}>
      {text}
    </div>
  );

  const subSectionTitle = (text, color) => (
    <div style={{ fontSize: 12, fontWeight: 700, color: color || T.accent, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10, paddingInlineStart: 4 }}>
      {text}
    </div>
  );

  const statBox = (value, label, color) => (
    <div style={{ flex: 1, minWidth: 90, background: 'rgba(150,150,150,0.07)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || T.accent, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.4 }}>{label}</div>
    </div>
  );

  const subjectBar = (sub) => {
    const name   = lang === 'ar' ? sub.name_ar : sub.name_en;
    const color  = scoreColor(sub.score);
    const isUp   = sub.change >= 0;
    const arrowColor = isUp ? '#22c55e' : '#ef4444';
    const changeStr  = `${isUp ? '▲' : '▼'} ${Math.abs(sub.change)}%`;
    return (
      <div key={name} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{name}</span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: arrowColor, fontWeight: 700 }}>{changeStr}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color, minWidth: 38, textAlign: 'end' }}>{sub.score}%</span>
          </div>
        </div>
        <ProgressBar value={sub.score} color={color} />
      </div>
    );
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) { setWeaknessPopup(null); onClose(); } }}
    >
      <style>{`@keyframes wr-fadein{from{opacity:0;transform:scale(0.94)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: dir }}>

        <div style={{ padding: '20px 26px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0, background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, transparent 60%)' }}>
          <div>
            <span style={{ display: 'inline-block', padding: '3px 12px', background: 'rgba(139,92,246,0.15)', color: '#8b5cf6', borderRadius: 20, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
              {lang === 'ar' ? 'تقرير أسبوعي' : 'Weekly Report'}
            </span>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary, marginBottom: 2 }}>
              {lang === 'ar' ? `${weekLabel}` : weekLabel}
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary }}>
              {lang === 'ar' ? `الطالب: ${studentName} | الصف الرابع` : `Student: ${studentName} | Grade ${report.grade}`}
            </div>
            <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{formatDate(report.created_at, lang)}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '40px 48px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 26 }}>

          <div>
            {sectionTitle(rt.weeklyReportTitle)}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
              {statBox(report.sessions_count, lang === 'ar' ? 'الجلسات' : 'Sessions', T.accent)}
              {statBox(report.subjects_count, lang === 'ar' ? 'المواد' : 'Subjects', '#8b5cf6')}
              {statBox(`${report.avg_performance}%`, lang === 'ar' ? 'متوسط الأداء' : 'Avg Performance', scoreColor(report.avg_performance))}
              {statBox(`${report.active_days_count}/7`, lang === 'ar' ? 'أيام النشاط' : 'Active Days', report.active_days_count >= 5 ? '#22c55e' : '#f59e0b')}
            </div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 10 }}>
              {lang === 'ar' ? 'نشاط هذا الأسبوع:' : "This week's activity:"}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {report.week_days.map((d, i) => {
                const label = lang === 'ar' ? d.day_ar : d.day_en;
                return (
                  <div key={i} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 16,
                      background: d.active ? 'rgba(5,150,105,0.85)' : 'rgba(150,150,150,0.12)',
                      border: d.active ? '1.5px solid rgba(5,150,105,0.5)' : '1px solid transparent',
                    }}>
                      {d.active ? '✅' : '❌'}
                    </div>
                    <span style={{ fontSize: 10, color: T.textSecondary }}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            {sectionTitle(lang === 'ar' ? 'جلسات هذا الأسبوع' : "This Week's Sessions")}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {report.sessions.map((s, i) => {
                const color = scoreColor(s.score);
                const date    = lang === 'ar' ? s.date_ar    : s.date_en;
                const subject = lang === 'ar' ? s.subject_ar : s.subject_en;
                const topic   = lang === 'ar' ? s.topic_ar   : s.topic_en;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(150,150,150,0.05)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 14px', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: T.textSecondary, minWidth: 90 }}>{date}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{subject}</span>
                      <span style={{ fontSize: 12, color: T.textSecondary }}>— {topic}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color, background: `${color}18`, borderRadius: 20, padding: '3px 10px' }}>{s.score}%</span>
                      <span style={{ fontSize: 12, color: T.textSecondary }}>{s.duration} {lang === 'ar' ? 'دقيقة' : 'min'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            {sectionTitle(rt.performanceBySubject)}
            {report.cambridge_subjects.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                {subSectionTitle(rt.cambridgeSection, T.accent)}
                {report.cambridge_subjects.map(sub => subjectBar(sub))}
              </div>
            )}
            {report.qatar_subjects.length > 0 && (
              <div>
                {subSectionTitle(rt.qatarSection, '#8b5cf6')}
                <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 10, fontStyle: 'italic' }}>
                  {rt.qatarNote}
                </div>
                {report.qatar_subjects.map(sub => subjectBar(sub))}
              </div>
            )}
          </div>

          <div>
            {sectionTitle(rt.weakPoints)}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {report.weekly_weaknesses.map((w, i) => {
                const wName = lang === 'ar' ? w.name : (w.name_en || w.name);
                return (
                  <span
                    key={i}
                    onClick={e => { e.stopPropagation(); setWeaknessPopup({ weakness: w.name, subjectName: lang === 'ar' ? 'هذا الأسبوع' : 'This Week', x: e.clientX, y: e.clientY }); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, fontSize: 13, color: '#ef4444', cursor: 'pointer', userSelect: 'none' }}
                    title={lang === 'ar' ? 'انقر للتفاصيل' : 'Click for details'}
                  >
                    <span style={{ fontWeight: 700 }}>●</span> {wName}
                  </span>
                );
              })}
            </div>
          </div>

          {report.weekly_recommendations && report.weekly_recommendations.length > 0 && (
            <div>
              {sectionTitle(rt.pedagogicalTitle)}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {report.weekly_recommendations.map((rec, i) => {
                  const rc   = REC_COLORS[rec.type] || REC_COLORS.blue;
                  const text = lang === 'ar' ? rec.text_ar : rec.text_en;
                  return (
                    <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: rc.bg, borderRadius: 10, padding: '12px 14px', borderInlineStart: `4px solid ${rc.border}` }}>
                      <span style={{ fontSize: 16, color: rc.border, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{rc.icon}</span>
                      <span style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <ActionPlanSection actionPlan={report.action_plan} rt={rt} reportLang={lang} />

        </div>

        <div style={{ padding: '14px 26px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
          <button style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => showToast(lang === 'ar' ? 'قريباً — ميزة تصدير PDF' : 'Coming soon — PDF export', 'info')}>
            {rt.exportPDF}
          </button>
          <button style={{ flex: 1, minWidth: 120, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => showToast(lang === 'ar' ? 'التقارير الحقيقية فقط يمكن إعادة إرسالها' : 'Only real reports can be resent', 'info')}>
            {rt.sendEmail}
          </button>
          <button style={{ flex: 2, minWidth: 140, padding: '10px 0', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={onClose}>
            {rt.close}
          </button>
        </div>

      </div>

      {weaknessPopup && (
        <WeaknessPopup data={weaknessPopup} lang={lang} T={T} onClose={() => setWeaknessPopup(null)} />
      )}
    </div>
  );
}

/* ───────────────────────────────────── ReportContent (real) ─────────── */

function ReportContent({ content }) {
  const { T } = useTheme();
  const lang = useReportLang();
  if (!content) return <p style={{ color: T.textSecondary }}>{lang === 'ar' ? 'لا يوجد محتوى' : 'No content'}</p>;
  if (content.includes('<') && content.includes('>')) {
    return <div style={{ fontSize: 15, lineHeight: 1.8, color: T.textPrimary }} dangerouslySetInnerHTML={{ __html: content }} />;
  }
  const HEADER_PATTERNS = ['القسم الأول', 'القسم الثاني', 'القسم الثالث', 'القسم الرابع', 'Section 1', 'Section 2'];
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.8, color: T.textPrimary }}>
      {content.split('\n').map((line, i) => {
        const isHeader = HEADER_PATTERNS.some(p => line.includes(p));
        return (
          <div key={i} style={{ fontSize: isHeader ? 17 : 15, fontWeight: isHeader ? 700 : 400, color: T.textPrimary, marginTop: isHeader ? 16 : 0, marginBottom: isHeader ? 6 : 0 }}>
            {line || '\u00A0'}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────── real report helpers ─────────────────── */

function adaptRadar(radarObj) {
  if (!radarObj) return null;
  if (radarObj.axes_ar && radarObj.axes_en) return radarObj;
  const axes = radarObj.axes || [];
  return { axes_ar: axes, axes_en: axes, scores: radarObj.scores || [] };
}

function renderFallbackContent(report, T, lang) {
  const content = report.content || '';
  if (!content) return <p style={{ color: T.textSecondary }}>{lang === 'ar' ? 'لا يوجد محتوى' : 'No content'}</p>;
  if (content.includes('<') && content.includes('>')) {
    return <div style={{ fontSize: 15, lineHeight: 1.8, color: T.textPrimary }} dangerouslySetInnerHTML={{ __html: content }} />;
  }
  const HEADER_PATTERNS = ['القسم الأول', 'القسم الثاني', 'Section 1', 'Section 2'];
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.8, color: T.textPrimary }}>
      {content.split('\n').map((line, i) => {
        const isHeader = HEADER_PATTERNS.some(p => line.includes(p));
        return (
          <div key={i} style={{ fontSize: isHeader ? 17 : 15, fontWeight: isHeader ? 700 : 400, color: T.textPrimary, marginTop: isHeader ? 16 : 0, marginBottom: isHeader ? 6 : 0 }}>
            {line || '\u00A0'}
          </div>
        );
      })}
    </div>
  );
}

function renderMonthlyContent(data, rt, T, lang, sectionTitle, statBox, setWeaknessPopup) {
  const cambridge = data.cambridge_subjects
    ? (data.cambridge_subjects || [])
    : (data.subjects || []).filter(s => s.curriculum !== 'qatar');
  const qatar = data.qatar_subjects
    ? (data.qatar_subjects || [])
    : (data.subjects || []).filter(s => s.curriculum === 'qatar');
  const allSubj = data.cambridge_subjects
    ? [...(data.cambridge_subjects || []), ...(data.qatar_subjects || [])]
    : (data.subjects || []);
  const correctPct = data.total_questions > 0
    ? Math.round((data.correct_answers / data.total_questions) * 100)
    : (data.avg_score || 0);
  const subList = (arr) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {arr.map((sub, i) => (
        <SubjectSection key={i} sub={sub} lang={lang} T={T} onWeaknessClick={setWeaknessPopup}
          radarData={sub.radar ? adaptRadar(sub.radar) : undefined} />
      ))}
    </div>
  );
  return (
    <>
      <div>
        {sectionTitle(rt.executiveSummary)}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {statBox(`${data.avg_score || 0}%`, lang === 'ar' ? 'متوسط الدرجة' : 'Avg Score', scoreColor(data.avg_score || 0))}
          {statBox(data.total_sessions || data.sessions_count || 0, lang === 'ar' ? 'إجمالي الجلسات' : 'Sessions', T.accent)}
          {statBox(data.total_questions || 0, lang === 'ar' ? 'إجمالي الأسئلة' : 'Questions', '#8b5cf6')}
          {statBox(`${correctPct}%`, lang === 'ar' ? 'نسبة الصحيحة' : 'Correct', scoreColor(correctPct))}
          {statBox(data.weaknesses_count || (data.weak_concepts || []).length || 0, lang === 'ar' ? 'نقاط الضعف' : 'Weaknesses', '#f59e0b')}
        </div>
      </div>
      {allSubj.length > 0 && (
        <div>
          {sectionTitle(rt.performanceBySubject)}
          {cambridge.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 12, paddingInlineStart: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {rt.cambridgeSection}
              </div>
              {subList(cambridge)}
            </div>
          )}
          {qatar.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{rt.qatarSection}</div>
                <div style={{ fontSize: 11, color: T.textSecondary, background: 'rgba(139,92,246,0.1)', borderRadius: 8, padding: '3px 8px' }}>{lang === 'ar' ? 'تُقيَّم بمعايير مستقلة' : 'Independent'}</div>
              </div>
              <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12, fontStyle: 'italic', lineHeight: 1.5 }}>{rt.qatarNote}</div>
              {subList(qatar)}
            </div>
          )}
          {cambridge.length === 0 && qatar.length === 0 && allSubj.length > 0 && subList(allSubj)}
        </div>
      )}
      {(data.streak_days != null || data.active_days) && (
        <div>
          {sectionTitle(rt.streakTitle)}
          <div style={{ marginBottom: 12, fontSize: 13, color: T.textSecondary }}>
            {lang === 'ar' ? `${data.streak_days || 0} أيام متتالية` : `${data.streak_days || 0} consecutive days`}
          </div>
          {data.active_days && <StreakCalendar activeDays={data.active_days} T={T} />}
        </div>
      )}
      {(data.recommendations || []).length > 0 && (
        <div>
          {sectionTitle(rt.pedagogicalTitle)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.recommendations || []).map((rec, i) => {
              const rc = REC_COLORS[rec.type] || REC_COLORS.blue;
              const text = lang === 'ar' ? rec.text_ar : rec.text_en;
              return (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: rc.bg, borderRadius: 10, padding: '12px 14px', borderInlineStart: `4px solid ${rc.border}` }}>
                  <span style={{ fontSize: 16, color: rc.border, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{rc.icon}</span>
                  <span style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function renderWeeklyContent(data, rt, T, lang, sectionTitle, setWeaknessPopup) {
  const cambridge = data.cambridge_subjects
    ? (data.cambridge_subjects || [])
    : (data.subjects || []).filter(s => s.curriculum !== 'qatar');
  const qatar = data.qatar_subjects
    ? (data.qatar_subjects || [])
    : (data.subjects || []).filter(s => s.curriculum === 'qatar');
  const allSubj = data.cambridge_subjects
    ? [...(data.cambridge_subjects || []), ...(data.qatar_subjects || [])]
    : (data.subjects || []);
  const statBox = (value, label, color) => (
    <div style={{ flex: 1, minWidth: 90, background: 'rgba(150,150,150,0.07)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || T.accent, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.4 }}>{label}</div>
    </div>
  );
  return (
    <>
      <div>
        {sectionTitle(rt.weeklyReportTitle)}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {statBox(data.sessions_count || 0, lang === 'ar' ? 'الجلسات' : 'Sessions', T.accent)}
          {statBox(data.subjects_count || allSubj.length || 0, lang === 'ar' ? 'المواد' : 'Subjects', '#8b5cf6')}
          {statBox(`${data.avg_score || data.avg_performance || 0}%`, lang === 'ar' ? 'متوسط الأداء' : 'Avg Performance', scoreColor(data.avg_score || data.avg_performance || 0))}
        </div>
      </div>
      {allSubj.length > 0 && (
        <div>
          {sectionTitle(rt.performanceBySubject)}
          {cambridge.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.accent, marginBottom: 12, paddingInlineStart: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{rt.cambridgeSection}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {cambridge.map((sub, i) => <SubjectSection key={i} sub={sub} lang={lang} T={T} onWeaknessClick={setWeaknessPopup} radarData={sub.radar ? adaptRadar(sub.radar) : undefined} />)}
              </div>
            </div>
          )}
          {qatar.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12, paddingInlineStart: 4 }}>{rt.qatarSection}</div>
              <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 12, fontStyle: 'italic', lineHeight: 1.5 }}>{rt.qatarNote}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {qatar.map((sub, i) => <SubjectSection key={i} sub={sub} lang={lang} T={T} onWeaknessClick={setWeaknessPopup} radarData={sub.radar ? adaptRadar(sub.radar) : undefined} />)}
              </div>
            </div>
          )}
          {cambridge.length === 0 && qatar.length === 0 && allSubj.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {allSubj.map((sub, i) => <SubjectSection key={i} sub={sub} lang={lang} T={T} onWeaknessClick={setWeaknessPopup} radarData={sub.radar ? adaptRadar(sub.radar) : undefined} />)}
            </div>
          )}
        </div>
      )}
      {(data.weak_concepts || []).length > 0 && (
        <div>
          {sectionTitle(rt.weakPoints)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(data.weak_concepts || []).map((w, i) => (
              <div key={i} style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 14, color: T.textPrimary }}>
                {typeof w === 'string' ? w : (lang === 'ar' ? w.name : (w.name_en || w.name))}
              </div>
            ))}
          </div>
        </div>
      )}
      {(data.recommendations || []).length > 0 && (
        <div>
          {sectionTitle(rt.pedagogicalTitle)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(data.recommendations || []).map((rec, i) => {
              const rc = REC_COLORS[rec.type] || REC_COLORS.blue;
              const text = lang === 'ar' ? rec.text_ar : rec.text_en;
              return (
                <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', background: rc.bg, borderRadius: 10, padding: '12px 14px', borderInlineStart: `4px solid ${rc.border}` }}>
                  <span style={{ fontSize: 16, color: rc.border, fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{rc.icon}</span>
                  <span style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.6 }}>{text}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

function renderSessionContent(data, rt, T, lang, sectionTitle, setWeaknessPopup) {
  const subject    = lang === 'ar' ? (data.subject_ar || data.subject) : (data.subject_en || data.subject);
  const unit       = lang === 'ar' ? (data.unit_ar || data.unit) : (data.unit_en || data.unit);
  const topic      = lang === 'ar' ? (data.topic_ar || data.topic) : (data.topic_en || data.topic);
  const summary    = lang === 'ar' ? (data.summary_ar || data.summary) : (data.summary_en || data.summary);
  const nextTopic  = lang === 'ar' ? (data.next_topic_ar || data.next_topic) : (data.next_topic_en || data.next_topic);
  const nextRec    = lang === 'ar' ? (data.next_rec_ar || data.next_rec) : (data.next_rec_en || data.next_rec);
  const concepts   = data.concepts || (data.concepts_covered || []).map(c =>
    typeof c === 'string' ? { name_ar: c, name_en: c, status: 'good', q_total: 1, q_correct: 1 } : c
  );
  const questions  = data.questions || [];
  const weaknesses = data.session_weaknesses || data.weaknesses || [];
  const radar      = data.session_radar ? adaptRadar(data.session_radar) : null;
  const correctCount = questions.filter(q => q.correct).length;
  const totalCount   = questions.length;
  const scorePct     = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : (data.score_percentage || 0);
  return (
    <>
      <div>
        {sectionTitle(rt.sessionSummary)}
        <div style={{ background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)', borderRadius: 12, padding: '16px 18px', marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {[
              { label: lang === 'ar' ? 'المادة'    : 'Subject', value: subject  || '—' },
              { label: lang === 'ar' ? 'الوحدة'    : 'Unit',    value: unit     || '—' },
              { label: lang === 'ar' ? 'الموضوع'   : 'Topic',   value: topic    || '—' },
              { label: lang === 'ar' ? 'النتيجة'   : 'Score',   value: `${scorePct}%`  },
            ].map((item, i) => (
              <div key={i} style={{ flex: 1, minWidth: 110, background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{item.value}</div>
              </div>
            ))}
          </div>
          {summary && <p style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.75, margin: 0 }}>{summary}</p>}
        </div>
      </div>
      {concepts.length > 0 && (
        <div>
          {sectionTitle(rt.conceptsCovered)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {concepts.map((c, i) => {
              const st      = CONCEPT_STATUS[c.status] || CONCEPT_STATUS.good;
              const name    = lang === 'ar' ? (c.name_ar || c.name) : (c.name_en || c.name);
              const stLabel = lang === 'ar' ? st.label_ar : st.label_en;
              const pct     = c.q_total > 0 ? Math.round(c.q_correct / c.q_total * 100) : 0;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(150,150,150,0.05)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '11px 14px', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ fontSize: 16 }}>{st.icon}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, color: st.color, fontWeight: 600 }}>{stLabel}</span>
                    {c.q_total > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(pct), background: `${scoreColor(pct)}18`, borderRadius: 20, padding: '3px 10px' }}>
                        {c.q_correct}/{c.q_total}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {radar && (
        <div>
          {sectionTitle(lang === 'ar' ? 'الأداء على المحاور' : 'Axis Performance')}
          <div style={{ flex: '0 0 auto', paddingInlineEnd: 56 }}>
            <SessionRadarChart radarData={radar} T={T} lang={lang} />
          </div>
        </div>
      )}
      {weaknesses.length > 0 && (
        <div>
          {sectionTitle(rt.weaknessesDetected)}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {weaknesses.map((w, i) => {
              const name = lang === 'ar' ? (w.name || w.name_ar) : (w.name_en || w.name);
              return (
                <div key={i} style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 14, color: T.textPrimary }}>
                  {name}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: T.textSecondary, fontStyle: 'italic' }}>{rt.autoSavedNote}</div>
        </div>
      )}
      {(nextTopic || nextRec) && (
        <div>
          {sectionTitle(rt.nextSession)}
          {nextTopic && (
            <div style={{ fontSize: 14, fontWeight: 600, color: T.accent, marginBottom: 8 }}>
              {lang === 'ar' ? `الموضوع القادم: ${nextTopic}` : `Next topic: ${nextTopic}`}
            </div>
          )}
          {nextRec && <p style={{ fontSize: 14, color: T.textPrimary, lineHeight: 1.7, margin: 0 }}>{nextRec}</p>}
        </div>
      )}
    </>
  );
}

function renderQuizContent(data, rt, T, lang, sectionTitle, setWeaknessPopup, filterTab, setFilterTab, showAll, setShowAll) {
  const questions = data.quiz_questions || data.questions || [];
  const radar     = data.quiz_radar ? adaptRadar(data.quiz_radar) : null;
  const score     = data.score || data.score_percentage || 0;
  const prevScore = data.prev_score || 0;
  const filtered  = filterTab === 'all' ? questions
    : filterTab === 'correct' ? questions.filter(q => q.correct)
    : questions.filter(q => !q.correct);
  const visible   = showAll ? filtered : filtered.slice(0, 8);
  const remaining = filtered.length - 8;
  const correctCount = questions.filter(q =>  q.correct).length;
  const wrongCount   = questions.filter(q => !q.correct).length;
  const sectionBox = (children) => (
    <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 22px', marginBottom: 18 }}>
      {children}
    </div>
  );
  const qSectionTitle = (text) => (
    <div style={{ fontWeight: 700, fontSize: 15, color: T.textPrimary, marginBottom: 14, borderBottom: `1.5px solid ${T.border}`, paddingBottom: 8 }}>
      {text}
    </div>
  );
  return (
    <>
      {sectionBox(<>
        {qSectionTitle(rt.quizOverview)}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
          {[
            { label_ar: 'إجمالي الأسئلة', label_en: 'Total Questions', value: data.total_questions || questions.length, color: T.accent },
            { label_ar: 'إجابات صحيحة',   label_en: 'Correct',         value: correctCount,                             color: '#22c55e' },
            { label_ar: 'إجابات خاطئة',   label_en: 'Wrong',           value: wrongCount,                               color: '#ef4444' },
            { label_ar: 'النتيجة السابقة', label_en: 'Previous Score',  value: `${prevScore}%`,                          color: '#f59e0b' },
          ].map((s, i) => (
            <div key={i} style={{ flex: '1 1 120px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 4 }}>{lang === 'ar' ? s.label_ar : s.label_en}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <ScoreCircle score={score} T={T} />
          {prevScore > 0 && (
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: T.textPrimary, marginBottom: 10 }}>
                {lang === 'ar' ? 'مقارنة بالاختبار السابق' : 'Comparison with Previous Quiz'}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{prevScore}%</div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{lang === 'ar' ? 'السابق' : 'Previous'}</div>
                </div>
                <div style={{ fontSize: 20, color: T.textSecondary }}>→</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: scoreColor(score) }}>{score}%</div>
                  <div style={{ fontSize: 11, color: T.textSecondary }}>{lang === 'ar' ? 'الحالي' : 'Current'}</div>
                </div>
                <div style={{ marginInlineStart: 8, fontWeight: 700, fontSize: 16, color: score >= prevScore ? '#22c55e' : '#ef4444' }}>
                  {score >= prevScore ? '▲' : '▼'} {Math.abs(score - prevScore)}%
                </div>
              </div>
            </div>
          )}
        </div>
      </>)}
      {radar && sectionBox(<>
        {qSectionTitle(rt.axisPerformance)}
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '0 0 auto', paddingInlineEnd: 56 }}>
            <RadarChart radarData={radar} T={T} lang={lang} />
          </div>
          <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(radar.scores || []).map((sc, i) => (
              <div key={i} style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: T.textSecondary, width: 140, flexShrink: 0 }}>
                  {lang === 'ar' ? (radar.axes_ar || [])[i] : (radar.axes_en || [])[i]}
                </span>
                <ProgressBar value={sc} color={scoreColor(sc)} />
                <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(sc), minWidth: 36, textAlign: 'start' }}>{sc}%</span>
              </div>
            ))}
          </div>
        </div>
      </>)}
      {questions.length > 0 && sectionBox(<>
        {qSectionTitle(rt.questionBreakdown)}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['all', 'correct', 'wrong'].map(tab => (
            <button key={tab} onClick={() => { setFilterTab(tab); setShowAll(false); }}
              style={{ padding: '6px 16px', borderRadius: 20, border: `1px solid ${filterTab === tab ? T.accent : T.border}`, background: filterTab === tab ? T.accent : 'transparent', color: filterTab === tab ? 'white' : T.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              {tab === 'all' ? rt.all : tab === 'correct' ? rt.correct : rt.wrong}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map((q, i) => {
            const qText    = lang === 'ar' ? (q.q_ar || q.q) : (q.q_en || q.q);
            const studentA = lang === 'ar' ? (q.student_ar || q.student_answer) : (q.student_en || q.student_answer);
            const correctA = lang === 'ar' ? (q.correct_ar || q.correct_answer) : (q.correct_en || q.correct_answer);
            return (
              <div key={i} style={{ borderRadius: 10, border: `1px solid ${q.correct ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`, padding: '12px 14px', background: q.correct ? 'rgba(34,197,94,0.04)' : 'rgba(239,68,68,0.04)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{q.correct ? '✅' : '❌'}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: T.textPrimary, lineHeight: 1.5 }}>{qText}</span>
                </div>
                {studentA && (
                  <div style={{ fontSize: 13, color: q.correct ? '#22c55e' : '#ef4444' }}>
                    {lang === 'ar' ? 'إجابة الطالب:' : 'Student answer:'} {studentA}
                  </div>
                )}
                {!q.correct && correctA && (
                  <div style={{ fontSize: 13, color: '#22c55e', marginTop: 4 }}>
                    {lang === 'ar' ? 'الإجابة الصحيحة:' : 'Correct answer:'} {correctA}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!showAll && remaining > 0 && (
          <button onClick={() => setShowAll(true)} style={{ marginTop: 12, padding: '8px 20px', borderRadius: 20, border: `1px solid ${T.border}`, background: 'transparent', color: T.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
            {rt.showMore} {remaining} {rt.questions}
          </button>
        )}
      </>)}
    </>
  );
}

function RealReportModal({ report, onClose, onResend }) {
  const { T }  = useTheme();
  const lang   = useReportLang();
  const rt     = reportTranslations[lang] || reportTranslations['ar'];
  const dir    = lang === 'ar' ? 'rtl' : 'ltr';
  const badge  = TYPE_BADGE[report.report_type] || { label: { ar: report.report_type, en: report.report_type }, color: T.textSecondary, bg: T.bgCardSolid };
  const [weaknessPopup, setWeaknessPopup] = useState(null);
  const [filterTab,     setFilterTab]     = useState('all');
  const [showAll,       setShowAll]       = useState(false);
  const data        = report.data;
  const studentName = data?.student_name || report.student_name || '';
  const sectionTitle = (text) => (
    <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, borderInlineStart: `4px solid ${T.accent}`, paddingInlineStart: 10, marginBottom: 14, marginTop: 6 }}>
      {text}
    </div>
  );
  const statBox = (value, label, color) => (
    <div style={{ flex: 1, minWidth: 90, background: 'rgba(150,150,150,0.07)', border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || T.accent, marginBottom: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.4 }}>{label}</div>
    </div>
  );
  let bodyContent;
  if (!data) {
    bodyContent = renderFallbackContent(report, T, lang);
  } else if (report.report_type === 'monthly_report' || report.report_type === 'monthly') {
    bodyContent = renderMonthlyContent(data, rt, T, lang, sectionTitle, statBox, setWeaknessPopup);
  } else if (report.report_type === 'weekly_report' || report.report_type === 'weekly') {
    bodyContent = renderWeeklyContent(data, rt, T, lang, sectionTitle, setWeaknessPopup);
  } else if (report.report_type === 'session_report' || report.report_type === 'session') {
    bodyContent = renderSessionContent(data, rt, T, lang, sectionTitle, setWeaknessPopup);
  } else if (report.report_type === 'quiz_report' || report.report_type === 'quiz' || report.report_type === 'exam') {
    bodyContent = renderQuizContent(data, rt, T, lang, sectionTitle, setWeaknessPopup, filterTab, setFilterTab, showAll, setShowAll);
  } else {
    bodyContent = renderFallbackContent(report, T, lang);
  }
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) { setWeaknessPopup(null); onClose(); } }}
    >
      <style>{`@keyframes wr-fadein{from{opacity:0;transform:scale(0.94)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: dir }}>
        {weaknessPopup && <WeaknessPopup data={weaknessPopup} lang={lang} T={T} onClose={() => setWeaknessPopup(null)} />}
        <div style={{ padding: '20px 26px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0, background: 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, transparent 60%)' }}>
          <div>
            <span style={{ display: 'inline-block', padding: '3px 12px', background: badge.bg, color: badge.color, borderRadius: 20, fontSize: 12, fontWeight: 600, marginBottom: 8 }}>{badge.label[lang]}</span>
            <div style={{ fontSize: 19, fontWeight: 800, color: T.textPrimary, marginBottom: 2 }}>{studentName}</div>
            <div style={{ fontSize: 13, color: T.textSecondary }}>{formatDate(report.created_at, lang)}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '40px 48px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 26 }}>
          {bodyContent}
          <ActionPlanSection actionPlan={report.action_plan} rt={rt} reportLang={lang} />
        </div>
        <div style={{ padding: '16px 28px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
          <button style={{ flex: 1, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => { onResend(report); onClose(); }}>{rt.sendEmail}</button>
          <button style={{ flex: 1, padding: '10px 0', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={onClose}>{rt.close}</button>
        </div>
      </div>
    </div>
  );
}

function ReportModal({ report, onClose, onResend }) {
  const { T } = useTheme();
  const lang = useReportLang();
  const rt = reportTranslations[lang] || reportTranslations['ar'];
  const tr = translations[useGuardianLanguage().lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const badge = TYPE_BADGE[report.report_type] || { label: { ar: report.report_type, en: report.report_type }, color: T.textSecondary, bg: T.bgCardSolid };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: dir }}>
        <div style={{ padding: '22px 28px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <span style={{ display: 'inline-block', padding: '3px 10px', background: badge.bg, color: badge.color, borderRadius: 20, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{badge.label[lang]}</span>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.textPrimary }}>{report.student_name}</div>
            <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>{formatDate(report.created_at, lang)}</div>
          </div>
          <button style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', lineHeight: 1, padding: 4 }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '40px 48px', overflowY: 'auto', flex: 1 }}>
          <ReportContent content={report.content} />
        </div>
        <div style={{ padding: '16px 28px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
          <button style={{ flex: 1, padding: '10px 0', borderRadius: 10, background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => { onResend(report); onClose(); }}>{rt.sendEmail}</button>
          <button style={{ flex: 1, padding: '10px 0', borderRadius: 12, background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={onClose}>{rt.close}</button>
        </div>
      </div>
    </div>
  );
}

function SkeletonCard() {
  const { T } = useTheme();
  return (
    <div style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, animation: 'gr-pulse 1.6s ease-in-out infinite' }}>
      <div>
        <div style={{ height: 20, width: 100, background: T.border, borderRadius: 10, marginBottom: 8 }} />
        <div style={{ height: 16, width: 140, background: T.border, borderRadius: 8, marginBottom: 6 }} />
        <div style={{ height: 13, width: 180, background: T.border, borderRadius: 8 }} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ height: 36, width: 90, background: T.border, borderRadius: 9 }} />
        <div style={{ height: 36, width: 110, background: T.border, borderRadius: 9 }} />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────── main page ─────────────────── */

export default function GuardianReports() {
  const navigate     = useNavigate();
  const { T, theme } = useTheme();
  const { lang }     = useGuardianLanguage();
  const reportLang   = useReportLang();
  const tr           = translations[lang];
  const dir          = lang === 'ar' ? 'rtl' : 'ltr';

  const guardianId = localStorage.getItem('jahbath_guardian_id');
  const token      = localStorage.getItem('jahbath_token');

  const [guardian,   setGuardian]   = useState({ first_name: '', email: '' });
  const [students,   setStudents]   = useState([]);
  const [reports,    setReports]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [viewReport, setViewReport] = useState(null);
  const { toast, show: showToast }  = useToast();

  const [filters, setFilters] = useState({ student_id: '', report_type: '', date_from: '', date_to: '' });
  const [applied, setApplied] = useState(filters);

  useEffect(() => {
    if (!token || !guardianId) { navigate('/guardian/auth', { replace: true }); return; }
    loadStudents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (guardianId) loadReports(applied, page);
  }, [applied, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStudents = async () => {
    try {
      const res  = await guardianFetch(`${API}/guardian/dashboard/${guardianId}`);
      if (!res) return;
      const json = await res.json();
      if (json.success) {
        setStudents(json.students || []);
        setGuardian(json.guardian || { first_name: '', email: '' });
      }
    } catch {}
  };

  const loadReports = useCallback(async (f, pg) => {
    if (!guardianId) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ page: pg });
      if (f.student_id)  params.set('student_id',  f.student_id);
      if (f.report_type) params.set('report_type', f.report_type);
      if (f.date_from)   params.set('date_from',   f.date_from);
      if (f.date_to)     params.set('date_to',     f.date_to);
      const res  = await guardianFetch(`${API}/guardian/reports/${guardianId}?${params}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res) return;
      const json = await res.json();
      if (json.success) {
        const raw = json.reports || [];
        raw.forEach(r => { if (!r.data && r.content) { try { r.data = JSON.parse(r.content); } catch { r.data = null; } } });
        setReports(raw); setTotal(json.total || 0);
      }
      else setError(lang === 'ar' ? 'تعذر جلب التقارير' : 'Failed to fetch reports');
    } catch {
      setError(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error');
    } finally {
      setLoading(false);
    }
  }, [guardianId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => { setPage(1); setApplied({ ...filters }); };

  const handleResend = async (report) => {
    try {
      const res  = await guardianFetch(`${API}/guardian/resend-report/${report.id}`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res) return;
      const json = await res.json();
      showToast(
        json.success
          ? (lang === 'ar' ? 'تم إرسال التقرير على بريدك الإلكتروني ✓' : 'Report sent to your email ✓')
          : (lang === 'ar' ? 'حدث خطأ، حاول مرة أخرى' : 'An error occurred, please try again'),
        json.success ? 'success' : 'error',
      );
    } catch {
      showToast(lang === 'ar' ? 'تعذر الاتصال بالخادم' : 'Server connection error', 'error');
    }
  };

  const handleLogout = async () => {
    await logoutAndClearLocal('guardian');
    navigate('/guardian/auth', { replace: true });
  };

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const displayReports = reports.length === 0 ? DUMMY_REPORTS : reports;
  const isDummy        = reports.length === 0;
  const totalPages     = isDummy ? 1 : Math.max(1, Math.ceil(total / 10));

  if (!token || !guardianId) return null;

  const inputStyle = { padding: '9px 12px', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)', border: `1px solid ${T.border}`, borderRadius: 12, color: T.textPrimary, fontSize: 14, outline: 'none', fontFamily: 'inherit' };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'transparent', color: T.textPrimary, fontFamily: "'Sora', sans-serif", direction: dir, transition: 'background 0.2s ease, color 0.2s ease' }}>
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{`@keyframes gr-pulse{0%,100%{opacity:.4}50%{opacity:.8}}`}</style>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=Nunito:wght@400;600;700;800&display=swap');
        @media (max-width: 767px) {
          main { width: 100% !important; padding: 12px !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important; }
          main [style*="font-size: 26px"] { font-size: 18px !important; }
          [style*="grid-template-columns"] { grid-template-columns: 1fr !important; }
          [style*="inset: 0"] > div { width: 95vw !important; max-width: 95vw !important; padding: 16px !important; margin: 10px auto !important; box-sizing: border-box !important; }
          table { display: block !important; overflow-x: auto !important; }
        }
        @media (min-width: 768px) and (max-width: 1024px) {
          main { padding: 20px !important; }
          [style*="grid-template-columns"] { grid-template-columns: repeat(2, 1fr) !important; }
          [style*="inset: 0"] > div { max-width: 680px !important; }
        }
      `}</style>
      <GuardianSidebar guardianName={guardian.first_name} guardianEmail={guardian.email} onLogout={handleLogout} />

      <main style={{ flex: 1, padding: '32px 36px', overflowY: 'auto', position: 'relative', zIndex: 10 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color: T.textPrimary, marginBottom: 4 }}>{tr.reports}</div>
        <div style={{ fontSize: 14, color: T.textSecondary, marginBottom: 28 }}>
          {lang === 'ar' ? 'تصفح وإدارة جميع تقارير أبنائك' : "Browse and manage all your students' reports"}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 500 }}>{tr.students}</span>
            <select style={{ ...inputStyle, minWidth: 140, cursor: 'pointer' }} value={filters.student_id} onChange={e => setFilters(f => ({ ...f, student_id: e.target.value }))}>
              <option value="">{tr.all}</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 500 }}>{lang === 'ar' ? 'نوع التقرير' : 'Report Type'}</span>
            <select style={{ ...inputStyle, minWidth: 140, cursor: 'pointer' }} value={filters.report_type} onChange={e => setFilters(f => ({ ...f, report_type: e.target.value }))}>
              {REPORT_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label[lang]}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 500 }}>{tr.from}</span>
            <input type="date" style={{ ...inputStyle, colorScheme: theme }} value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: T.textSecondary, fontWeight: 500 }}>{tr.to}</span>
            <input type="date" style={{ ...inputStyle, colorScheme: theme }} value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          </div>
          <button style={{ padding: '9px 24px', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 14, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', fontFamily: 'inherit' }} onClick={handleSearch}>
            {tr.search}
          </button>
        </div>

        {loading ? (
          [...Array(4)].map((_, i) => <SkeletonCard key={i} />)
        ) : error ? (
          <div style={{ color: T.error, fontSize: 15, padding: '20px 0' }}>{error}</div>
        ) : (
          <>
            {isDummy && (
              <div style={{ marginBottom: 16, padding: '10px 16px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 10, fontSize: 13, color: '#b45309' }}>
                {lang === 'ar' ? 'لا توجد تقارير حقيقية بعد — يُعرض هنا نموذج تقرير توضيحي.' : 'No real reports yet — sample reports are shown for preview.'}
              </div>
            )}
            {displayReports.map(r => {
              const badge = TYPE_BADGE[r.report_type] || { label: { ar: r.report_type, en: r.report_type }, color: T.textSecondary, bg: T.bgCardSolid };
              const name  = lang === 'ar' ? r.student_name : (r.student_name_en || r.student_name);
              return (
                <div key={r.id} style={{ background: T.bgCardSolid, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12, transition: 'border-color 0.2s ease' }}>
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', background: badge.bg, color: badge.color, borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{badge.label[lang]}</span>
                      {isDummy && <span style={{ fontSize: 11, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', borderRadius: 20, padding: '2px 8px' }}>{lang === 'ar' ? 'نموذج' : 'Sample'}</span>}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary, marginBottom: 2 }}>{name}</div>
                    <div style={{ fontSize: 13, color: T.textSecondary }}>{formatDate(r.created_at, lang)}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button style={{ padding: '8px 18px', background: 'linear-gradient(135deg, #059669, #10B981)', color: 'white', border: 'none', borderRadius: 12, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => setViewReport(r)}>
                      {tr.viewReport}
                    </button>
                    {!isDummy && (
                      <button style={{ padding: '8px 18px', background: 'transparent', color: T.textSecondary, border: `1px solid ${T.border}`, borderRadius: 9, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }} onClick={() => handleResend(r)}>
                        {tr.sendEmail}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {!isDummy && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 }}>
                <button style={{ padding: '8px 20px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', color: page <= 1 ? T.textMuted : T.textPrimary, fontSize: 14, cursor: page <= 1 ? 'default' : 'pointer', fontFamily: 'inherit' }} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>{tr.prev}</button>
                <span style={{ padding: '8px 16px', color: T.textSecondary, fontSize: 14, alignSelf: 'center' }}>{tr.page} {page} {tr.of} {totalPages}</span>
                <button style={{ padding: '8px 20px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', color: page >= totalPages ? T.textMuted : T.textPrimary, fontSize: 14, cursor: page >= totalPages ? 'default' : 'pointer', fontFamily: 'inherit' }} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>{tr.next}</button>
              </div>
            )}
          </>
        )}
      </main>

      {viewReport && (
        viewReport.id?.startsWith('dummy') ? (
          viewReport.id === 'dummy-002' ? (
            <SessionReportModal report={viewReport} onClose={() => setViewReport(null)} onResend={handleResend} showToast={showToast} lang={reportLang} />
          ) : viewReport.id === 'dummy-003' ? (
            <QuizReportModal report={viewReport} onClose={() => setViewReport(null)} showToast={showToast} lang={reportLang} />
          ) : viewReport.id === 'dummy-004' ? (
            <WeeklyReportModal report={viewReport} onClose={() => setViewReport(null)} onResend={handleResend} showToast={showToast} lang={reportLang} />
          ) : (
            <DummyReportModal report={viewReport} onClose={() => setViewReport(null)} onResend={handleResend} showToast={showToast} lang={reportLang} />
          )
        ) : (
          <RealReportModal report={viewReport} onClose={() => setViewReport(null)} onResend={handleResend} />
        )
      )}

      <Toast toast={toast} />
    </div>
  );
}
